import { embedAuditEvidenceDigest } from './auditEvidenceDigest.js';
import { renderEvidenceHTML } from './diagnosticEvidence.js';
import { renderAuditEvidenceContractHTML, renderInpScenarioScopesHTML, renderInpScenarioFailureNotice } from './auditEvidencePresentation.js';
import { publicAssetUrl } from '../config/publicOrigin.js';
// html2canvas + jsPDF are heavy (~500KB combined). They are imported
// dynamically inside the async export functions below so they stay out of the
// initial/shared bundle and are only fetched when a user actually exports.

import {
  lcpCauseContent,
  inpCauseContent,
  severityLabels,
  severityColor,
  genericCodeExampleLabel,
  inpGenericCodeExampleLabel,
  longTaskScopeDetail,
  longTaskCountDiscrepancy,
  validLongTasks,
  inpCauseDescription,
  inpCauseRecommendationsFor,
  inpDiagnosticSeverity,
  normalizeInpDiagnosticCause
} from './causeContent.js';
import { ownerForRecommendation, buildFollowUpSchedule, formatFollowUpDate, followUpLabels } from './findingOwners.js';
import { hasGtmContainer, scopeTagRecommendations } from './gtmAdvice.js';
import { metricCopy } from './metricCopy.js';
import { METRIC_THRESHOLDS } from './metricThresholds.js';
import { renderInteractionDiagnosticsHtml, renderMultiPageInteractionDiagnosticsHtml } from './inpInteractionDiagnostics.js';
import { hasValidInpMeasurement } from './inpMeasurementValidity.js';
import { canonicalFindingSentences, deriveCanonicalFinding } from './canonicalFinding.js';
import { getDeepFailurePresentation } from './deepFailurePresentation.js';
import { formatConsentState } from './consentState.js';
import { comparableLabSummary } from './comparableLabSummary.js';

const generateStyles = () => `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: #e2e8f0;
    padding: 40px;
    min-height: 100vh;
  }
  .export-container {
    max-width: 1200px;
    margin: 0 auto;
    background: #1e293b;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid #334155;
  }
  .logo { font-size: 24px; font-weight: 700; color: #3b82f6; }
  .date { font-size: 12px; color: #64748b; }
  .url-info {
    background: #0f172a;
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 24px;
    border: 1px solid #334155;
  }
  .url-label { font-size: 12px; color: #94a3b8; margin-bottom: 4px; }
  .url-value { font-size: 14px; color: #60a5fa; word-break: break-all; }
  .metric-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    margin-bottom: 24px;
  }
  .metric-badge.good { background: #059669; color: white; }
  .metric-badge.neutral { background: #475569; color: white; }
  .metric-badge.needs-improvement { background: #d97706; color: white; }
  .metric-badge.poor { background: #dc2626; color: white; }
  .section {
    background: #0f172a;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #334155;
  }
  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #f97316;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .summary-box {
    background: linear-gradient(135deg, #312e81 0%, #581c87 100%);
    border: 1px solid #6366f1;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
  }
  .summary-text { font-size: 14px; line-height: 1.6; color: #e0e7ff; }
  .phases-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 16px;
  }
  .phase-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 12px;
    text-align: center;
  }
  .phase-value { font-size: 20px; font-weight: 700; }
  .phase-value.good { color: #22c55e; }
  .phase-value.warning { color: #f59e0b; }
  .phase-label { font-size: 11px; color: #64748b; margin-top: 4px; }
  .element-item {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .element-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }
  .element-description {
    display: inline-block;
    padding: 4px 10px;
    background: #6366f1;
    color: white;
    font-size: 11px;
    font-weight: 600;
    border-radius: 12px;
  }
  .element-selector {
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 12px;
    color: #22d3ee;
    background: #0f172a;
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 8px;
    word-break: break-all;
  }
  .element-path {
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 10px;
    color: #64748b;
    background: #0f172a50;
    padding: 6px 10px;
    border-radius: 4px;
    margin-bottom: 8px;
    word-break: break-all;
  }
  .element-text {
    font-size: 11px;
    color: #a5b4fc;
    background: #312e8150;
    padding: 8px 10px;
    border-radius: 4px;
    margin-bottom: 8px;
    font-style: italic;
  }
  .element-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 12px;
    color: #94a3b8;
  }
  .element-shift { color: #f59e0b; font-weight: 600; }
  .task-card {
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .task-card.critical { background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); }
  .task-card.high { background: rgba(249, 115, 22, 0.1); border: 1px solid rgba(249, 115, 22, 0.3); }
  .task-card.medium { background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.3); }
  .task-priority {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  .task-priority.critical { background: #dc2626; color: white; }
  .task-priority.high { background: #ea580c; color: white; }
  .task-priority.medium { background: #d97706; color: white; }
  .task-title { font-size: 14px; font-weight: 600; color: white; margin-bottom: 8px; }
  .task-description { font-size: 12px; color: #94a3b8; margin-bottom: 12px; }
  .task-fix {
    background: #0f172a;
    border-radius: 6px;
    padding: 12px;
    font-size: 11px;
    font-family: 'Monaco', 'Consolas', monospace;
    color: #a5b4fc;
    white-space: pre-wrap;
  }
  .screenshot-container {
    margin: 20px 0;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #334155;
  }
  .screenshot-container img {
    width: 100%;
    height: auto;
    display: block;
  }
  .screenshot-caption {
    background: #1e293b;
    padding: 12px;
    font-size: 12px;
    color: #94a3b8;
    text-align: center;
  }
  .impact-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin: 16px 0;
  }
  .impact-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 16px;
    text-align: center;
  }
  .impact-label { font-size: 11px; color: #a855f7; margin-bottom: 8px; }
  .impact-text { font-size: 12px; color: #cbd5e1; }
  .recommendations-list {
    list-style: none;
    padding: 0;
  }
  .recommendations-list li {
    padding: 12px 16px;
    background: #1e293b;
    border-left: 3px solid #3b82f6;
    margin-bottom: 8px;
    font-size: 13px;
    color: #cbd5e1;
  }
  .footer {
    margin-top: 40px;
    padding-top: 24px;
    border-top: 1px solid #334155;
    text-align: center;
    font-size: 12px;
    color: #64748b;
  }
  .w3c-section { background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%); }
  .w3c-badge {
    display: inline-block;
    padding: 2px 8px;
    background: #3b82f6;
    color: white;
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
    margin-left: 8px;
  }
  .timing-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    margin-top: 12px;
  }
  .timing-item {
    text-align: center;
    padding: 12px 8px;
    background: #1e293b;
    border-radius: 8px;
  }
  .timing-value { font-size: 14px; font-weight: 600; color: #22c55e; }
  .timing-label { font-size: 10px; color: #64748b; margin-top: 4px; }
  .resource-bar {
    height: 8px;
    background: #334155;
    border-radius: 4px;
    overflow: hidden;
    margin: 8px 0;
  }
  .resource-fill {
    height: 100%;
    border-radius: 4px;
  }
  .resource-fill.js { background: #fbbf24; }
  .resource-fill.css { background: #ec4899; }
  .resource-fill.img { background: #06b6d4; }
  @media print {
    body { background: white; color: #1e293b; }
    .export-container { box-shadow: none; }
    .section { page-break-inside: avoid; }
  }
  .screenshot-container img {
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .screenshot-container img:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
  }
  .lightbox-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    z-index: 9999;
    justify-content: center;
    align-items: center;
    cursor: zoom-out;
  }
  .lightbox-overlay.active { display: flex; }
  .lightbox-overlay img {
    max-width: 95%;
    max-height: 95%;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5);
  }
  .lightbox-close {
    position: absolute;
    top: 20px;
    right: 30px;
    font-size: 32px;
    color: white;
    cursor: pointer;
    z-index: 10000;
    opacity: 0.8;
    transition: opacity 0.2s;
  }
  .lightbox-close:hover { opacity: 1; }
  .action-plan-section { background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%); }
  .client-summary {
    background: #0f172a;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 20px;
    border-left: 4px solid #3b82f6;
  }
  .impact-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin: 20px 0;
  }
  .impact-card-detailed {
    background: #1e293b;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #334155;
  }
  .impact-card-detailed.seo { border-top: 3px solid #ef4444; }
  .impact-card-detailed.ux { border-top: 3px solid #f97316; }
  .impact-card-detailed.conversion { border-top: 3px solid #f59e0b; }
  .dev-task {
    background: #1e293b;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    border-left: 4px solid;
  }
  .dev-task.critical { border-left-color: #ef4444; background: rgba(239, 68, 68, 0.05); }
  .dev-task.high { border-left-color: #f97316; background: rgba(249, 115, 22, 0.05); }
  .dev-task.medium { border-left-color: #f59e0b; background: rgba(245, 158, 11, 0.05); }
  .dev-task-priority {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 12px;
  }
  .dev-task-priority.critical { background: #ef4444; color: white; }
  .dev-task-priority.high { background: #f97316; color: white; }
  .dev-task-priority.medium { background: #f59e0b; color: white; }
  .dev-task-selector {
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 12px;
    color: #22d3ee;
    background: #0f172a;
    padding: 8px 12px;
    border-radius: 6px;
    margin: 12px 0;
    word-break: break-all;
  }
  .dev-task-solution {
    background: #0f172a;
    border-radius: 8px;
    padding: 16px;
    margin-top: 12px;
  }
  .dev-task-solution-title {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
    margin-bottom: 8px;
  }
  .dev-task-solution ol {
    margin: 0;
    padding-left: 20px;
    color: #a5b4fc;
    font-size: 13px;
    line-height: 1.8;
  }
`;

const formatDate = (lang) => {
  const now = new Date();
  const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  return now.toLocaleDateString(lang === 'cs' ? 'cs-CZ' : 'en-US', options);
};

const getMetricStatus = (metric, value) => {
  const t = METRIC_THRESHOLDS[metric?.toLowerCase()] || METRIC_THRESHOLDS.cls;
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needs-improvement';
  return 'poor';
};

const causeLabels = {
  cs: {
    'consent-banner': 'Cookie consent banner',
    'font-swap': 'Webfonty (FOUT)',
    'ad-injection': 'Reklamní skripty',
    'unsized-image': 'Obrázky bez rozměrů',
    'iframe-embed': 'Vložené prvky (iframe)',
    'video-embed': 'Vložené prvky (video)',
    'dynamic-content': 'Dynamický obsah',
    'animation': 'Animace',
    'image-load': 'Načítání obrázků',
    'unknown': 'Neznámá příčina'
  },
  en: {
    'consent-banner': 'Cookie consent banner',
    'font-swap': 'Web fonts (FOUT)',
    'ad-injection': 'Ad scripts',
    'unsized-image': 'Unsized images',
    'iframe-embed': 'Embedded content (iframe)',
    'video-embed': 'Embedded content (video)',
    'dynamic-content': 'Dynamic content',
    'animation': 'Animations',
    'image-load': 'Image loading',
    'unknown': 'Unknown cause'
  }
};

const priorityLabels = {
  cs: { critical: 'Kritické', high: 'Vysoká', medium: 'Střední' },
  en: { critical: 'Critical', high: 'High', medium: 'Medium' }
};

const savingsLabels = {
  cs: {
    title: 'Doporučení s odhadem zlepšení',
    total: 'až',
    opportunityNote: 'měřená příležitost / modelový odhad, ne zaručená úspora',
    nonAdditive: 'Odhady se překrývají a nejsou aditivní — jednotlivé úspory nelze prostě sčítat.',
    countSuffix: 'doporučení',
    impactHigh: 'velká úspora',
    impactMedium: 'střední úspora',
    impactLow: 'malá úspora',
    effort: 'úsilí',
    effortLow: 'nízká',
    effortMedium: 'střední',
    effortHigh: 'vysoká',
    range: 'rozsah',
    howComputed: 'Jak to počítáme',
    badgePsi: 'Měřená příležitost (Google PSI)',
    badgeHigh: 'Vysoká důvěra',
    badgeMedium: 'Střední odhad',
    badgeLow: 'Přibližný odhad',
    methodology: 'Odhady kombinují přímá data z Google PageSpeed Insights (Lighthouse opportunities), kalibrované koeficienty z naší databáze měření a heuristiky podle typu a velikosti zdrojů. Hodnota se zobrazuje s rozsahem (min–max) a se značkou důvěry. Milisekundové odhady z Lighthouse se vztahují k simulovanému načtení stránky — nepřenášejí se 1:1 do odezvy na interakci (INP). Skutečný výsledek závisí na implementaci, hostingu a chování uživatelů — po nasazení změny doporučujeme znovu změřit.'
  },
  en: {
    title: 'Recommendations with estimated improvement',
    total: 'up to',
    opportunityNote: 'measured opportunity / model estimate, not a guaranteed saving',
    nonAdditive: 'Estimates overlap and are not additive — individual savings cannot simply be summed.',
    countSuffix: 'recommendations',
    impactHigh: 'large saving',
    impactMedium: 'medium saving',
    impactLow: 'small saving',
    effort: 'effort',
    effortLow: 'low',
    effortMedium: 'medium',
    effortHigh: 'high',
    range: 'range',
    howComputed: 'How we calculate this',
    badgePsi: 'Measured opportunity (Google PSI)',
    badgeHigh: 'High confidence',
    badgeMedium: 'Medium estimate',
    badgeLow: 'Approximate estimate',
    methodology: 'Estimates combine direct data from Google PageSpeed Insights (Lighthouse opportunities), calibrated coefficients from our measurement database, and heuristics based on resource type and size. The value is shown with a range (min–max) and a confidence badge. Millisecond estimates from Lighthouse relate to the simulated page load — they do not translate 1:1 into interaction responsiveness (INP). Actual results depend on implementation, hosting and user behavior — re-measure after deploying the change.'
  }
};

const formatKb = (bytes) => {
  const kb = (bytes || 0) / 1024;
  return kb >= 1024 ? `~${(kb / 1024).toFixed(1)} MB` : `~${Math.round(kb)} KB`;
};

// Unified presentation: whenever a byte size is known, KB is the primary
// unit ("~1 020 KB (~280 ms)") so the same recommendation never flips
// between "KB" in one run and "ms" in the next.
export const formatSavingsValue = (est) => {
  if (!est) return '—';
  if (est.unit === 'bytes') return formatKb(est.value);
  if (est.bytes > 0) return `${formatKb(est.bytes)} (~${Math.round(est.value || 0)} ms)`;
  return `~${Math.round(est.value || 0)} ms`;
};

export const formatSavingsRange = (est) => {
  if (!est?.range) return null;
  if (est.unit === 'bytes') {
    return `${Math.round((est.range.min || 0) / 1024)}–${Math.round((est.range.max || 0) / 1024)} KB`;
  }
  return `${Math.round(est.range.min || 0)}–${Math.round(est.range.max || 0)} ms`;
};

export const savingsBadgeKey = (rec) => {
  const src = rec.estimatedSavings?.source;
  // PSI ms values are Lighthouse simulated-load estimates — only byte
  // savings from PSI deserve the "exact" badge.
  if ((src === 'psi' || src === 'psi_direct') && rec.estimatedSavings?.unit !== 'ms') return 'badgePsi';
  const c = rec.confidenceBadge?.color;
  if (c === 'green') return 'badgeHigh';
  if (c === 'yellow') return 'badgeMedium';
  return 'badgeLow';
};

export const getSavingsLabels = (lang) => savingsLabels[lang] || savingsLabels.cs;

const formatLcpMs = (v) => {
  if (v == null) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${Math.round(v)}ms`;
};

export const generateDiagnosticsHTML = ({
  url,
  metric,
  value,
  fieldValue = null,
  diagnostics,
  deepCLSResults,
  multiPageResults,
  deepINPResults,
  deepDeviceRuns = [],
  inpCompareResults = null,
  multiPageINPResults,
  multiPageLCPResults,
  multiPageTTFBResults = null,
  deepLCPResults,
  deepTTFBResults = null,
  deepAnalyzedUrl = null,
  lcpFieldEvidence = null,
  screenshots = [],
  tasks = [],
  language = 'cs',
  includeW3C = false,
  includeAdvanced = false,
  includeActionPlan = false,
  device = null,
  savingsRecommendations = [],
  savingsSummary = null,
  failedDeepMetrics = [],
  deepFailures = {},
  devicePriority = null,
  auditDate = null,
  evidenceContract = null
}) => {
  const lang = language;
  const containerEvidence = hasGtmContainer(diagnostics);
  savingsRecommendations = scopeTagRecommendations(savingsRecommendations, diagnostics);
  const metricLowerKey = metric?.toLowerCase();
  const lcpSpread = deepLCPResults?.labRunSpread;
  const lcpSpreadValue = lcpSpread?.verified === true
    && lcpSpread?.evidenceStatus === 'verified'
    && lcpSpread?.basis === 'median'
    && Number.isFinite(Number(lcpSpread?.median))
    ? Number(lcpSpread.median)
    : deepLCPResults?.lcp;
  const validDeepInp = hasValidInpMeasurement(deepINPResults);
  const metricUpperKey = String(metric || '').toUpperCase();
  const failedMetrics = Array.isArray(failedDeepMetrics)
    ? failedDeepMetrics.map(item => String(item).toUpperCase())
    : [];
  const primaryDeepFailed = failedMetrics.includes(metricUpperKey)
    && !(metricLowerKey === 'inp' ? validDeepInp
      : metricLowerKey === 'lcp' ? deepLCPResults?.lcp != null
      : metricLowerKey === 'cls' ? deepCLSResults?.cls != null
      : false);
  const primaryFailure = deepFailures?.[metricUpperKey] || null;
  const primarySweep = metricLowerKey === 'inp' ? multiPageINPResults
    : metricLowerKey === 'lcp' ? multiPageLCPResults
      : metricLowerKey === 'cls' ? multiPageResults
        : metricLowerKey === 'ttfb' ? multiPageTTFBResults
          : null;
  const failurePresentation = primaryDeepFailed
    ? getDeepFailurePresentation(primaryFailure, lang, {
        metric: metricUpperKey,
        hasSweep: !!primarySweep,
        hasDiagnostics: !!diagnostics
      })
    : null;
  const deepMetricValue = metricLowerKey === 'cls' ? deepCLSResults?.cls
    : metricLowerKey === 'inp' ? (validDeepInp ? deepINPResults.inp : null)
    : metricLowerKey === 'lcp' ? lcpSpreadValue
    : null;
  const suppliedValue = metricLowerKey === 'inp' && !(Number(value) > 0) ? null : value;
  const diagnosticValue = metricLowerKey === 'inp'
    && !(Number(diagnostics?.[metricLowerKey]?.value) > 0)
    ? null
    : diagnostics?.[metricLowerKey]?.value;
  const metricValue = suppliedValue
    ?? deepMetricValue
    ?? diagnosticValue
    ?? null;
  const status = metricValue == null ? 'neutral' : getMetricStatus(metric, metricValue);

  const formatMetricValue = (m, v) => {
    if (v === null || v === undefined || isNaN(v)) return 'N/A';
    const metricLower = m?.toLowerCase();
    if (metricLower === 'cls') return v.toFixed(3);
    return Math.round(v) + 'ms';
  };

  // XSS-safe HTML escaping helper (used throughout export)
  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  let html = `<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ControlVitals - ${metric?.toUpperCase() || 'Diagnostics'} ${lang === 'cs' ? 'Diagnostika' : 'Diagnostics'}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>${generateStyles()}</style>
</head>
<body>
  <div class="export-container">
    <div class="header">
      <div>
        <div class="logo">ControlVitals</div>
        <div class="date">${formatDate(lang)}</div>
      </div>
      <div class="metric-badge ${status}">
        ${metric?.toUpperCase() || 'VITALS'}: ${formatMetricValue(metric, metricValue)}
      </div>
    </div>

    <div class="url-info">
      <div class="url-label">${lang === 'cs' ? 'Analyzovaná URL' : 'Analyzed URL'}${device ? ` · ${device === 'PHONE' || device === 'mobile' ? (lang === 'cs' ? 'Mobil' : 'Mobile') : device === 'TABLET' || device === 'tablet' ? 'Tablet' : device === 'DESKTOP' || device === 'desktop' ? 'Desktop' : device === 'ALL' ? (lang === 'cs' ? 'Všechna zařízení' : 'All devices') : device}` : ''}</div>
      <div class="url-value">${escapeHtml(url || 'N/A')}</div>
      ${metricLowerKey === 'lcp' && deepAnalyzedUrl && String(deepAnalyzedUrl) !== String(url) ? `
      <div class="url-label" style="margin-top:10px;">${lang === 'cs' ? 'URL hloubkové LCP diagnostiky' : 'Deep LCP diagnostics URL'}</div>
      <div class="url-value">${escapeHtml(deepAnalyzedUrl)}</div>` : ''}
    </div>`;

  if (primaryDeepFailed) {
    html += `
    <div class="section" style="border:1px solid #ef444480;background:#7f1d1d22;">
      <div class="section-title" style="color:#fca5a5;">${lang === 'cs'
        ? `NEKOMPLETNÍ REPORT — hloubková analýza ${escapeHtml(metricUpperKey)} selhala`
        : `INCOMPLETE REPORT — ${escapeHtml(metricUpperKey)} deep analysis failed`}</div>
      <div style="font-size:12px;color:#e2e8f0;line-height:1.65;">
        <p><b>${escapeHtml(failurePresentation?.labels?.cause || (lang === 'cs' ? 'Pravděpodobná příčina' : 'Likely cause'))}:</b> ${escapeHtml(failurePresentation?.cause || '')}</p>
        <p style="margin-top:8px;"><b>${escapeHtml(failurePresentation?.labels?.available || (lang === 'cs' ? 'Co je stále k dispozici' : 'Data still available'))}:</b> ${escapeHtml(failurePresentation?.available || '')}</p>
        <p style="margin-top:8px;"><b>${escapeHtml(failurePresentation?.labels?.next || (lang === 'cs' ? 'Další krok' : 'Next step'))}:</b> ${escapeHtml(failurePresentation?.action || '')}</p>
        <p style="margin-top:8px;color:#fca5a5;">${lang === 'cs'
          ? 'Opakované měření může dodat platná data, ale nelze předem slíbit, že určí konkrétní příčinu nebo odpovědného řešitele.'
          : 'A repeat measurement may provide valid data, but cannot promise in advance that it will identify a specific cause or owner.'}</p>
      </div>
    </div>`;
  }

  if (metricLowerKey === 'inp') {
    const finding = deriveCanonicalFinding({ metric, language, deepINPResults, device });
    html += `<section class="section"><h2>${lang === 'cs' ? 'Důkazy INP — příčina nepotvrzena' : 'INP evidence — cause unconfirmed'}</h2>${canonicalFindingSentences(finding).map(sentence => `<p>${escapeHtml(sentence)}</p>`).join('')}</section>`;
  }

  if (metricLowerKey === 'lcp' && lcpFieldEvidence) {
    const pageField = lcpFieldEvidence.analyzedPage;
    const authoritative = lcpFieldEvidence.authoritative || {};
    const samePageAvailable = pageField?.available === true && Number.isFinite(Number(pageField.p75));
    const spreadRange = lcpSpread?.runs
      ? `${formatMetricValue('LCP', lcpSpread.min)}–${formatMetricValue('LCP', lcpSpread.max)}, ${lang === 'cs' ? 'medián' : 'median'} ${formatMetricValue('LCP', deepMetricValue)}`
      : formatMetricValue('LCP', deepMetricValue);
    const phaseSummary = lcpSpread?.phaseSummary
      ? ['ttfb', 'resourceLoad', 'renderDelay'].map(key => {
          const summary = lcpSpread.phaseSummary[key];
          return summary
            ? `${key} ${formatMetricValue('LCP', summary.min)}–${formatMetricValue('LCP', summary.max)} (${lang === 'cs' ? 'medián' : 'median'} ${formatMetricValue('LCP', summary.median)})`
            : `${key} —`;
        }).join(' · ')
      : null;
    const variability = lcpSpread?.variabilityAttribution === 'observed-lcp-source-change'
      ? (lang === 'cs'
        ? 'Pozorovaný LCP zdroj se mezi běhy měnil. To je slučitelné s proměnlivým obsahem nebo stavem načtení, ale neprokazuje příčinu rozptylu.'
        : 'The observed LCP source changed between runs. This is consistent with changing content or load state, but does not prove the cause of the spread.')
      : lcpSpread?.variabilityAttribution === 'observed-third-party-set-change'
        ? (lang === 'cs'
          ? 'Mezi běhy se měnila zachycená sada externích blokujících zdrojů. To je slučitelné s proměnlivým third-party načítáním, ale neprokazuje příčinu rozptylu.'
          : 'The captured external blocking-resource set changed between runs. This is consistent with varying third-party loading, but does not prove the cause of the spread.')
        : lcpSpread?.variabilityAttribution === 'controlled-run-residual'
          && lcpSpread?.verified === true && lcpSpread?.conditionsConsistent !== false
          && lcpSpread?.attemptConditionsComplete !== false
          && ['protocol', 'captureMethod', 'effectiveCmpMode', 'cpuThrottling', 'networkProfile']
            .every(key => lcpSpread?.conditions?.[key] != null)
          ? (lang === 'cs'
            ? 'Zachycený LCP zdroj a evidované podmínky zůstaly stejné; zbývající rozptyl označujeme jen jako variabilitu mezi kontrolovanými lab běhy.'
            : 'The captured LCP source and recorded conditions stayed the same; the residual spread is labelled only as controlled lab run-to-run variance.')
          : (lang === 'cs'
            ? 'Evidence nestačí k rozlišení proměnlivého obsahu, third-party vlivu a zbytkového laboratorního rozptylu.'
            : 'The evidence is insufficient to distinguish changing content, third-party influence, and residual lab variance.');
    html += `
    <div class="section" style="border-color:#f59e0b70;">
      <div class="section-title" style="color:#fbbf24;">${lang === 'cs' ? 'LCP: zdroj a srovnatelnost dat' : 'LCP: data source and comparability'}</div>
      <div style="font-size:12px;color:#cbd5e1;line-height:1.65;">
        <p><b>${lang === 'cs' ? 'Cold lab série' : 'Cold lab series'}:</b> ${escapeHtml(spreadRange)} · ${escapeHtml(deepAnalyzedUrl || url || '')}</p>
        ${phaseSummary ? `<p style="margin-top:8px;"><b>${lang === 'cs' ? 'Fáze série' : 'Series phases'}:</b> ${escapeHtml(phaseSummary)}</p>` : ''}
        <p style="margin-top:8px;">${samePageAvailable
          ? (lang === 'cs'
            ? `CrUX má pro tutéž URL page-level p75 ${formatMetricValue('LCP', pageField.p75)}.`
            : `CrUX has page-level p75 ${formatMetricValue('LCP', pageField.p75)} for the same URL.`)
          : pageField?.error
            ? (lang === 'cs'
              ? 'Dostupnost page-level CrUX pro tutéž URL se nepodařilo ověřit.'
              : 'Same-URL page-level CrUX availability could not be verified.')
            : (lang === 'cs'
              ? 'CrUX pro tutéž URL nemá dostatečná page-level data; origin hodnotu proto nevydáváme za data této stránky.'
              : 'CrUX has insufficient page-level data for the same URL; the origin value is therefore not presented as data for this page.')}
        ${Number.isFinite(Number(authoritative.p75))
          ? (lang === 'cs'
            ? ` Autoritativní verdikt zůstává field p75 ${formatMetricValue('LCP', authoritative.p75)} pro origin a zařízení ${escapeHtml(lcpFieldEvidence.device || pageField?.formFactor || '')}; lab hodnota jej nemění.`
            : ` The authoritative verdict remains field p75 ${formatMetricValue('LCP', authoritative.p75)} for the origin and device ${escapeHtml(lcpFieldEvidence.device || pageField?.formFactor || '')}; the lab value does not change it.`)
          : ''}</p>
        <p style="margin-top:8px;">${lang === 'cs'
          ? 'Cold lab používá pevný profil, jeden testovací bod a prázdnou cache; field p75 shrnuje skutečná zařízení, sítě a stavy cache v okně CrUX.'
          : 'The cold lab uses a fixed profile, one test location, and an empty cache; field p75 aggregates real devices, networks, and cache states over the CrUX window.'}</p>
        <p style="margin-top:8px;">${escapeHtml(variability)}</p>
      </div>
    </div>`;
  }

  if (fieldValue != null && !isNaN(fieldValue) && deepMetricValue != null && !isNaN(deepMetricValue)) {
    const fieldStatus = getMetricStatus(metric, fieldValue);
    const labStatus = getMetricStatus(metric, deepMetricValue);
    const statusColor = (s) => s === 'good' ? '#22c55e' : s === 'needs-improvement' ? '#f59e0b' : s === 'poor' ? '#ef4444' : '#94a3b8';
    const diverge = fieldStatus !== labStatus;
    html += `
    <div class="section" style="border: 1px solid ${diverge ? '#f59e0b50' : '#334155'};">
      <div class="section-title" style="color: #22d3ee;">📊 ${lang === 'cs' ? 'Reální uživatelé vs. laboratorní test' : 'Real users vs. lab test'}</div>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 16px; text-align: center;">
          <div style="font-size: 11px; color: #94a3b8; margin-bottom: 6px;">${lang === 'cs' ? 'Reální uživatelé (CrUX, 28 dní)' : 'Real users (CrUX, 28 days)'}</div>
          <div style="font-size: 24px; font-weight: 700; color: ${statusColor(fieldStatus)};">${formatMetricValue(metric, fieldValue)}</div>
        </div>
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 16px; text-align: center;">
          <div style="font-size: 11px; color: #94a3b8; margin-bottom: 6px;">${lang === 'cs' ? 'Laboratorní test (tento report)' : 'Lab test (this report)'}</div>
          <div style="font-size: 24px; font-weight: 700; color: ${statusColor(labStatus)};">${formatMetricValue(metric, deepMetricValue)}</div>
        </div>
      </div>
      ${diverge ? `
      <div style="font-size: 12px; color: #cbd5e1; padding: 10px 12px; background: #f59e0b10; border: 1px solid #f59e0b30; border-radius: 6px; line-height: 1.6;">
        ${(() => {
          // Lab-side description comes from the shared metric-copy map —
          // no call site composes its own mechanism wording. Missing combo
          // → the note names no lab mechanism at all (safety: empty beats
          // wrong-metric copy).
          const labDesc = metricCopy(metric, 'labDivergence', lang === 'cs' ? 'cs' : 'en');
          if (!labDesc) {
            return lang === 'cs'
              ? 'Hodnoty se liší, protože měří něco jiného — CrUX je 75. percentil skutečných uživatelů za posledních 28 dní. Pro hodnocení webu Googlem jsou rozhodující data reálných uživatelů.'
              : 'The values differ because they measure different things — CrUX is the 75th percentile of real users over the last 28 days. Google ranks your site based on real-user data.';
          }
          // Dovětek o tom, CO field data navíc zahrnují, je také vázaný
          // na mechanismus metriky (u TTFB nejsou "pomalejší telefony"
          // relevantní — TTFB končí před během na klientovi).
          const tail = metricCopy(metric, 'fieldContextTail', lang === 'cs' ? 'cs' : 'en');
          const tailPart = tail ? ` — ${tail}` : '';
          return lang === 'cs'
            ? `Hodnoty se liší, protože měří něco jiného: ${labDesc}, zatímco CrUX je 75. percentil skutečných uživatelů za posledních 28 dní${tailPart}. Pro hodnocení webu Googlem jsou rozhodující data reálných uživatelů.`
            : `The values differ because they measure different things: ${labDesc}, while CrUX is the 75th percentile of real users over the last 28 days${tailPart}. Google ranks your site based on real-user data.`;
        })()}
      </div>` : ''}
    </div>`;
  }

  if (deepCLSResults) {
    const cls = deepCLSResults.cls || 0;
    const elements = deepCLSResults.elements || [];
    const phases = deepCLSResults.phases || {};

    const causeCounts = {};
    elements.forEach(el => {
      const cause = el.cause || 'unknown';
      causeCounts[cause] = (causeCounts[cause] || 0) + 1;
    });

    const biggestShifter = elements.length > 0
      ? elements.reduce((max, el) => (el.totalShift || 0) > (max.totalShift || 0) ? el : max, elements[0])
      : null;

    html += `
    <div class="section" style="background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%); border: 1px solid #3b82f6;">
      <div class="section-title" style="color: #60a5fa;">🔬 ${lang === 'cs' ? 'Hloubková analýza (Lab)' : 'Deep Analysis (Lab)'} <span style="font-size: 10px; padding: 2px 8px; background: ${deepCLSResults.skipCmp ? '#22c55e20' : '#f59e0b20'}; color: ${deepCLSResults.skipCmp ? '#22c55e' : '#f59e0b'}; border-radius: 12px; margin-left: 8px;">${formatConsentState(deepCLSResults, lang)}</span></div>
      <div style="font-size: 11px; color: #94a3b8; margin-bottom: 12px; padding: 8px; background: #0f172a50; border-radius: 6px;">
        ${lang === 'cs'
          ? 'Lab test = jednorázová simulace této stránky. CrUX = průměr od reálných uživatelů za 28 dní na celém originu.'
          : 'Lab test = one-time simulation of this page. CrUX = average from real users over 28 days on entire origin.'}
      </div>
    </div>`;

    const summaryParts = [];

    if (deepCLSResults?.cmpFiltered) {
      summaryParts.push(lang === 'cs'
        ? '💡 Shifty způsobené cookie consent bannerem byly odfiltrovány z výsledků.'
        : '💡 Shifts caused by cookie consent banner were filtered from results.');
    }

    if (biggestShifter) {
      const selector = biggestShifter.selector || biggestShifter.tagName || 'unknown';
      const shortSelector = selector.length > 50 ? selector.substring(0, 47) + '...' : selector;
      summaryParts.push(lang === 'cs'
        ? `Největší posun způsobuje "${escapeHtml(shortSelector)}" (shift: ${biggestShifter.totalShift?.toFixed(3)}).`
        : `The biggest shift is caused by "${escapeHtml(shortSelector)}" (shift: ${biggestShifter.totalShift?.toFixed(3)}).`);
    }

    const causeList = Object.entries(causeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([cause, count]) => {
        const name = escapeHtml(causeLabels[lang]?.[cause] || cause);
        return count > 1 ? `${name} (${count}×)` : name;
      });

    if (causeList.length > 0) {
      summaryParts.push(lang === 'cs'
        ? `Detekované příčiny: ${causeList.join(', ')}.`
        : `Detected causes: ${causeList.join(', ')}.`);
    }

    let assessment;
    if (cls <= METRIC_THRESHOLDS.cls.good) {
      assessment = lang === 'cs'
        ? `CLS je v pořádku (nejvýše ${METRIC_THRESHOLDS.cls.good}).`
        : `CLS is good (at or below ${METRIC_THRESHOLDS.cls.good}).`;
    } else if (cls <= METRIC_THRESHOLDS.cls.poor) {
      assessment = lang === 'cs'
        ? `CLS potřebuje zlepšení (${METRIC_THRESHOLDS.cls.good}–${METRIC_THRESHOLDS.cls.poor}).`
        : `CLS needs improvement (${METRIC_THRESHOLDS.cls.good}–${METRIC_THRESHOLDS.cls.poor}).`;
    } else {
      assessment = lang === 'cs'
        ? `CLS je špatné a vyžaduje okamžitou pozornost (nad ${METRIC_THRESHOLDS.cls.poor}).`
        : `CLS is poor and needs immediate attention (above ${METRIC_THRESHOLDS.cls.poor}).`;
    }
    summaryParts.push(assessment);

    html += `
    <div class="summary-box">
      <div class="section-title" style="color: #a5b4fc;">📋 ${lang === 'cs' ? 'Shrnutí' : 'Summary'}</div>
      <div class="summary-text">${summaryParts.join('<br><br>')}</div>

      ${phases.initial !== undefined ? `
      <div class="phases-grid">
        <div class="phase-card">
          <div class="phase-value ${phases.initial > 0.05 ? 'warning' : 'good'}">${(phases.initial || 0).toFixed(3)}</div>
          <div class="phase-label">${lang === 'cs' ? 'Načtení' : 'Initial'}</div>
        </div>
        <div class="phase-card">
          <div class="phase-value ${phases.scroll > 0.05 ? 'warning' : 'good'}">${(phases.scroll || 0).toFixed(3)}</div>
          <div class="phase-label">${lang === 'cs' ? 'Scroll' : 'Scroll'}</div>
        </div>
        <div class="phase-card">
          <div class="phase-value ${phases.postScroll > 0.05 ? 'warning' : 'good'}">${(phases.postScroll || 0).toFixed(3)}</div>
          <div class="phase-label">${lang === 'cs' ? 'Async' : 'Async'}</div>
        </div>
      </div>
      ` : ''}
    </div>`;

    if (elements.length > 0) {
      html += `
    <div class="section">
      <div class="section-title">📍 ${lang === 'cs' ? 'Elementy způsobující shift' : 'Elements causing shift'}</div>`;

      elements.slice(0, 10).forEach(el => {
        const description = el.elementDescription || el.friendlyName;
        const visibleText = el.visibleText;
        const fullPath = el.fullPath;
        const ariaLabel = el.ariaLabel;

        html += `
      <div class="element-item">
        <div class="element-header">
          ${description ? `<span class="element-description">${escapeHtml(description)}</span>` : ''}
          <span class="element-shift">shift: ${(el.totalShift || el.score || 0).toFixed(4)}</span>
        </div>
        <div class="element-selector">${escapeHtml(el.selector || el.tagName || 'Unknown element')}</div>
        ${fullPath ? `<div class="element-path">📍 ${escapeHtml(fullPath)}</div>` : ''}
        ${visibleText ? `<div class="element-text">"${escapeHtml(visibleText)}"</div>` : ''}
        ${ariaLabel ? `<div class="element-text">aria-label: "${escapeHtml(ariaLabel)}"</div>` : ''}
        <div class="element-meta">
          <span>${escapeHtml(causeLabels[lang]?.[el.cause] || el.cause || '')}</span>
          ${el.dimensions ? `<span>${el.dimensions.width}px × ${el.dimensions.height}px</span>` : ''}
        </div>
      </div>`;
      });

      html += `
    </div>`;
    }
  }

  if (deepLCPResults) {
    const lcp = deepLCPResults.lcp || 0;
    const phases = deepLCPResults.phases || {};
    const lcpElement = deepLCPResults.lcpElement || deepLCPResults.element || null;
    const causes = Array.isArray(deepLCPResults.causes) ? deepLCPResults.causes : [];
    const recommendations = Array.isArray(deepLCPResults.recommendations) ? deepLCPResults.recommendations : [];
    const screenshot = deepLCPResults.screenshot || null;
    const skipCmp = !!deepLCPResults.skipCmp;

    const lcpStatus = getMetricStatus('lcp', lcp);
    const lcpColor = lcpStatus === 'good' ? '#22c55e' : (lcpStatus === 'needs-improvement' ? '#f59e0b' : '#ef4444');
    const fmtMs = (ms) => ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;

    const lcpCauseLabels = lcpCauseContent.labels;
    const lcpCauseDescriptions = lcpCauseContent.descriptions;
    const lcpCauseRecommendations = lcpCauseContent.recommendations;
    const lcpCauseExamples = lcpCauseContent.examples;
    const sevColor = severityColor;
    const sevLabel = (s) => (severityLabels[s] && severityLabels[s][lang]) || s;

    html += `
    <div class="section" style="background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%); border: 1px solid #f97316;">
      <div class="section-title" style="color: #fb923c;">🔬 ${lang === 'cs' ? 'Hloubková LCP analýza (Lab)' : 'Deep LCP Analysis (Lab)'} <span style="font-size: 10px; padding: 2px 8px; background: ${skipCmp ? '#22c55e20' : '#f59e0b20'}; color: ${skipCmp ? '#22c55e' : '#f59e0b'}; border-radius: 12px; margin-left: 8px;">${formatConsentState(deepLCPResults, lang)}</span></div>
      <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
        <span style="display: inline-block; padding: 8px 16px; background: ${lcpColor}; color: white; font-weight: 600; border-radius: 8px;">LCP: ${fmtMs(lcp)}</span>
        <span style="font-size: 12px; color: #94a3b8;">${lang === 'cs'
          ? `Cíl: nejvýše ${(METRIC_THRESHOLDS.lcp.good / 1000).toFixed(1)} s`
          : `Target: at or below ${(METRIC_THRESHOLDS.lcp.good / 1000).toFixed(1)} s`}</span>
      </div>
      ${(() => {
        const loadingMs = phases.resourceLoad ?? phases.loadTime;
        const known = (v) => v != null && Number.isFinite(Number(v));
        const hasAny = known(phases.ttfb) || known(loadingMs) || known(phases.renderDelay);
        if (!hasAny) return '';
        // An undetermined phase (null) renders as "nelze určit" — never as a
        // 0 ms that would falsely imply a known fast phase.
        const cell = (v, warnAt, label) => known(v)
          ? `<div class="phase-value ${Number(v) > warnAt ? 'warning' : 'good'}">${fmtMs(Number(v))}</div><div class="phase-label">${label}</div>`
          : `<div class="phase-value" style="color:#94a3b8;">${lang === 'cs' ? 'nelze určit' : 'undetermined'}</div><div class="phase-label">${label}</div>`;
        return `
      <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; margin-bottom: 8px;">${lang === 'cs' ? 'Fázový breakdown' : 'Phase Breakdown'}</div>
      <div class="phases-grid">
        <div class="phase-card">${cell(phases.ttfb, METRIC_THRESHOLDS.ttfb.good, 'TTFB')}</div>
        <div class="phase-card">${cell(loadingMs, 1500, lang === 'cs' ? 'Načítání' : 'Loading')}</div>
        <div class="phase-card">${cell(phases.renderDelay, 500, lang === 'cs' ? 'Render' : 'Render')}</div>
      </div>`;
      })()}
    </div>`;

    if (lcpElement || screenshot) {
      const elSelector = lcpElement?.selector || lcpElement?.tagName || '';
      const elUrl = lcpElement?.url || lcpElement?.src || '';
      const rectW = lcpElement?.rect?.width;
      const rectH = lcpElement?.rect?.height;
      const elSize = lcpElement?.size
        || (rectW != null && rectH != null ? `${Math.round(rectW)}x${Math.round(rectH)}px` : '')
        || (lcpElement?.width && lcpElement?.height ? `${lcpElement.width}x${lcpElement.height}px` : '');
      const rawScreenshot = screenshot ? (typeof screenshot === 'string' ? screenshot : (screenshot.data || screenshot.url || screenshot.src)) : null;
      const screenshotSrc = rawScreenshot
        ? (rawScreenshot.startsWith('data:') || rawScreenshot.startsWith('http') || rawScreenshot.startsWith('/')
            ? rawScreenshot
            : `data:image/png;base64,${rawScreenshot}`)
        : null;
      html += `
    <div class="section">
      <div class="section-title">🎯 ${lang === 'cs' ? 'LCP Element' : 'LCP Element'}</div>
      ${screenshotSrc ? `
      <div class="screenshot-container" style="margin-bottom: 12px;">
        <img src="${escapeHtml(screenshotSrc)}" alt="${lang === 'cs' ? 'LCP element screenshot' : 'LCP element screenshot'}" style="max-height: 360px; object-fit: contain;">
        <div class="screenshot-caption">${lang === 'cs' ? 'Screenshot LCP elementu' : 'LCP Element Screenshot'}</div>
      </div>` : ''}
      ${elSelector ? `<div class="element-selector">${escapeHtml(elSelector)}</div>` : ''}
      ${elUrl ? `<div style="font-size: 11px; color: #60a5fa; word-break: break-all; padding: 6px 10px; background: #0f172a; border-radius: 4px; margin-bottom: 8px; font-family: 'Monaco', 'Consolas', monospace;">${escapeHtml(elUrl)}</div>` : ''}
      ${elSize ? `<div style="font-size: 12px; color: #94a3b8;">${lang === 'cs' ? 'Velikost' : 'Size'}: ${escapeHtml(elSize)}</div>` : ''}
    </div>`;
    }

    if (causes.length > 0) {
      html += `
    <div class="section">
      <div class="section-title">⚠️ ${lang === 'cs' ? 'Příčiny a doporučení pro vývoj' : 'Causes & Development Recommendations'}</div>`;
      causes.forEach(cause => {
        const key = cause.cause;
        const label = lcpCauseLabels[key]?.[lang] || key || (lang === 'cs' ? 'Neznámá příčina' : 'Unknown cause');
        const desc = lcpCauseDescriptions[key]?.[lang] || cause.description || '';
        const recs = lcpCauseRecommendations[key]?.[lang] || [];
        const ex = lcpCauseExamples[key];
        const sev = cause.severity || 'medium';
        const color = sevColor(sev);
        html += `
      <div class="task-card" style="background: ${color}1a; border: 1px solid ${color}4d;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span class="task-priority" style="background: ${color}; color: white;">${escapeHtml(sevLabel(sev))}</span>
          <span style="font-size: 14px; font-weight: 600; color: white;">${escapeHtml(label)}</span>
        </div>
        ${desc ? `<div style="font-size: 12px; color: #cbd5e1; margin-bottom: 10px;">${escapeHtml(desc)}</div>` : ''}
        ${cause.description && cause.description !== desc ? `<div style="font-size: 11px; color: #94a3b8; font-style: italic; margin-bottom: 10px;">${escapeHtml(cause.description)}</div>` : ''}
        ${Array.isArray(cause.resources) && cause.resources.length > 0 ? `
        <div style="margin-bottom: 10px;">
          ${cause.resources.slice(0, 5).map(r => {
            const u = typeof r === 'string' ? r : (r?.url || JSON.stringify(r));
            return `<code style="display: block; font-size: 11px; color: #94a3b8; background: #0f172a; padding: 6px 10px; border-radius: 4px; margin-bottom: 4px; word-break: break-all; font-family: 'Monaco', 'Consolas', monospace;">${escapeHtml(u)}</code>`;
          }).join('')}
        </div>` : ''}
        ${Array.isArray(cause.fonts) && cause.fonts.length > 0 ? `
        <div style="margin-bottom: 10px;">
          ${cause.fonts.slice(0, 5).map(f => `<code style="display: block; font-size: 11px; color: #22d3ee; background: #0f172a; padding: 6px 10px; border-radius: 4px; margin-bottom: 4px; font-family: 'Monaco', 'Consolas', monospace;">${escapeHtml(f.fontFamily || '')} (font-display: ${escapeHtml(f.fontDisplay || 'auto')})</code>`).join('')}
        </div>` : ''}
        ${recs.length > 0 ? `
        <div style="margin-bottom: 10px;">
          <div style="font-size: 11px; font-weight: 600; color: #cbd5e1; margin-bottom: 6px;">${lang === 'cs' ? 'Doporučení:' : 'Fix:'}</div>
          <ul style="margin-left: 18px; color: #94a3b8; font-size: 12px; line-height: 1.6;">
            ${recs.map(r => `<li>${escapeHtml(r)}</li>`).join('')}
          </ul>
        </div>` : ''}
        ${ex ? `
        <div style="margin-top: 10px;">
          <div style="font-size: 11px; font-weight: 600; color: #cbd5e1; margin-bottom: 6px;">${lang === 'cs' ? 'Obecné kandidátní vzory:' : 'Generic candidate patterns:'}</div>
          <div style="font-size: 10px; color: #94a3b8; line-height: 1.5; margin-bottom: 6px;">${escapeHtml(genericCodeExampleLabel[lang])}</div>
          <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px; padding: 10px; margin-bottom: 6px;">
            <div style="font-size: 10px; color: #f87171; margin-bottom: 4px;">✗ ${lang === 'cs' ? 'Problémový kód' : 'Problem code'}</div>
            <pre style="font-size: 11px; color: #fca5a5; white-space: pre-wrap; font-family: 'Monaco', 'Consolas', monospace; margin: 0;">${escapeHtml(ex.bad)}</pre>
          </div>
          <div style="background: rgba(34, 197, 94, 0.1); border: 1px solid rgba(34, 197, 94, 0.3); border-radius: 6px; padding: 10px;">
            <div style="font-size: 10px; color: #4ade80; margin-bottom: 4px;">✓ ${lang === 'cs' ? 'Opravený kód' : 'Fixed code'}</div>
            <pre style="font-size: 11px; color: #86efac; white-space: pre-wrap; font-family: 'Monaco', 'Consolas', monospace; margin: 0;">${escapeHtml(ex.good)}</pre>
          </div>
        </div>` : ''}
      </div>`;
      });
      html += `
    </div>`;
    }

    {
      const causeKeys = new Set(causes.map(c => c?.cause).filter(Boolean));
      const extra = recommendations.filter(rec => {
        if (!rec) return false;
        if (typeof rec === 'string') return true;
        if (rec.cause && causeKeys.has(rec.cause)) return false;
        return true;
      });
      if (extra.length > 0) {
      html += `
    <div class="section">
      <div class="section-title">💡 ${lang === 'cs' ? 'Další doporučení' : 'Additional Recommendations'}</div>
      <ul class="recommendations-list">
        ${extra.map(rec => {
          if (typeof rec === 'string') return `<li>${escapeHtml(rec)}</li>`;
          const t = rec.title ? `<strong>${escapeHtml(rec.title)}</strong>` : '';
          const d = rec.description ? `<div style="margin-top: 4px; opacity: 0.9;">${escapeHtml(rec.description)}</div>` : '';
          const f = rec.fix ? `<pre style="margin-top: 8px; font-size: 11px; color: #67e8f9; background: #0f172a; padding: 8px; border-radius: 4px; white-space: pre-wrap; font-family: 'Monaco', 'Consolas', monospace;">${escapeHtml(rec.fix)}</pre>` : '';
          return `<li>${t}${d}${f}</li>`;
        }).join('')}
      </ul>
    </div>`;
      }
    }
  }

  const clsElements = deepCLSResults?.elements || [];
  const hasScreenshots = clsElements.some(el => el.screenshot || el.contextScreenshot);

  if (hasScreenshots) {
    html += `
    <div class="section">
      <div class="section-title">📸 ${lang === 'cs' ? 'Vizuální náhled' : 'Visual Preview'}</div>`;

    clsElements.filter(el => el.screenshot || el.contextScreenshot).forEach(el => {
      const selectorLabel = escapeHtml(el.selector || '');

      if (el.contextScreenshot) {
        const contextUrl = publicAssetUrl(el.contextScreenshot);
        html += `
      <div style="margin-bottom: 16px;">
        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; margin-bottom: 6px;">📍 ${lang === 'cs' ? 'Kontext na stránce' : 'Page Context'} <span style="color: #3b82f6; font-size: 9px;">(${lang === 'cs' ? 'klikněte pro otevření v nové záložce' : 'click to open in new tab'})</span></div>
        <div class="screenshot-container" style="margin: 0;">
          <a href="${escapeHtml(contextUrl)}" target="_blank" rel="noopener noreferrer" style="display: block;">
            <img src="${escapeHtml(contextUrl)}" alt="${lang === 'cs' ? 'Kontext na stránce' : 'Page context'}" style="max-height: 300px; object-fit: contain; cursor: pointer;">
          </a>
          <div class="screenshot-caption">${lang === 'cs' ? 'Červený rámeček označuje problematický element' : 'Red border marks the problematic element'}</div>
        </div>
      </div>`;
      }

      if (el.screenshot) {
        const screenshotUrl = publicAssetUrl(el.screenshot);
        html += `
      <div style="margin-bottom: 16px;">
        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; margin-bottom: 6px;">🔍 ${lang === 'cs' ? 'Detail elementu' : 'Element Detail'} <span style="color: #3b82f6; font-size: 9px;">(${lang === 'cs' ? 'klikněte pro otevření v nové záložce' : 'click to open in new tab'})</span></div>
        <div class="screenshot-container" style="margin: 0;">
          <a href="${escapeHtml(screenshotUrl)}" target="_blank" rel="noopener noreferrer" style="display: block;">
            <img src="${escapeHtml(screenshotUrl)}" alt="${selectorLabel}" style="max-height: 200px; object-fit: contain; cursor: pointer;">
          </a>
          <div class="screenshot-caption">${selectorLabel}</div>
        </div>
      </div>`;
      }
    });

    html += `
    </div>`;
  } else if (screenshots && screenshots.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">📸 ${lang === 'cs' ? 'Vizuální náhled' : 'Visual Preview'}</div>`;

    screenshots.forEach((screenshot, i) => {
      html += `
      <div class="screenshot-container">
        <img src="${escapeHtml(screenshot.data)}" alt="${escapeHtml(screenshot.caption || `Screenshot ${i + 1}`)}">
        ${screenshot.caption ? `<div class="screenshot-caption">${escapeHtml(screenshot.caption)}</div>` : ''}
      </div>`;
    });

    html += `
    </div>`;
  }

  // === SECTION: Problematic Elements with Code ===
  const lcpDiag = diagnostics?.lcp;
  const clsDiag = diagnostics?.cls;
  const inpDiag = diagnostics?.inp;
  const layoutShiftAttr = diagnostics?.layoutShiftAttribution;

  const hasCodeSnippets = (lcpDiag?.element?.node) ||
    (clsDiag?.elements?.some(el => el.node || el.snippet)) ||
    (inpDiag?.elements?.some(el => el.node || el.snippet)) ||
    (layoutShiftAttr?.elements?.some(el => el.snippet)) ||
    (deepCLSResults?.elements?.some(el => el.node || el.snippet || el.selector));

  if (hasCodeSnippets) {
    html += `
    <div class="section">
      <div class="section-title">🔍 ${lang === 'cs' ? 'Problémové elementy - zdrojový kód' : 'Problematic Elements - Source Code'}</div>
      <p style="font-size: 12px; color: #94a3b8; margin-bottom: 16px;">${lang === 'cs' ? 'Níže jsou uvedeny konkrétní HTML elementy které způsobují problémy s výkonem.' : 'Below are the specific HTML elements causing performance issues.'}</p>`;

    // LCP Element
    if (lcpDiag?.element?.node) {
      html += `
      <div style="margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 12px; font-weight: 600; color: #22c55e;">LCP Element</span>
          <span style="font-size: 10px; padding: 2px 8px; background: #22c55e20; color: #22c55e; border-radius: 12px;">${escapeHtml(lcpDiag.element.type || 'element')}</span>
        </div>
        ${lcpDiag.element.selector ? `<div style="font-size: 11px; color: #64748b; margin-bottom: 6px;">Selector: <code style="background: #1e293b; padding: 2px 6px; border-radius: 4px;">${escapeHtml(lcpDiag.element.selector)}</code></div>` : ''}
        <pre style="background: #0f172a; border: 1px solid #334155; border-radius: 8px; padding: 12px; overflow-x: auto; font-size: 11px; color: #e2e8f0; font-family: 'JetBrains Mono', monospace; margin: 0;"><code>${escapeHtml(lcpDiag.element.node)}</code></pre>
      </div>`;
    }

    // CLS Elements from diagnostics.cls
    if (clsDiag?.elements?.length > 0) {
      clsDiag.elements.filter(el => el.node || el.snippet).slice(0, 5).forEach((el, i) => {
        const code = el.node || el.snippet;
        html += `
      <div style="margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 12px; font-weight: 600; color: #f59e0b;">CLS Element ${i + 1}</span>
          ${el.score ? `<span style="font-size: 10px; padding: 2px 8px; background: #f59e0b20; color: #f59e0b; border-radius: 12px;">score: ${el.score.toFixed(4)}</span>` : ''}
        </div>
        ${el.selector ? `<div style="font-size: 11px; color: #64748b; margin-bottom: 6px;">Selector: <code style="background: #1e293b; padding: 2px 6px; border-radius: 4px;">${escapeHtml(el.selector)}</code></div>` : ''}
        <pre style="background: #0f172a; border: 1px solid #334155; border-radius: 8px; padding: 12px; overflow-x: auto; font-size: 11px; color: #e2e8f0; font-family: 'JetBrains Mono', monospace; margin: 0;"><code>${escapeHtml(code)}</code></pre>
      </div>`;
      });
    }

    // Layout Shift Attribution elements
    if (layoutShiftAttr?.elements?.length > 0) {
      layoutShiftAttr.elements.filter(el => el.snippet).slice(0, 5).forEach((el, i) => {
        html += `
      <div style="margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 12px; font-weight: 600; color: #f59e0b;">Layout Shift ${i + 1}</span>
          ${el.score ? `<span style="font-size: 10px; padding: 2px 8px; background: #f59e0b20; color: #f59e0b; border-radius: 12px;">score: ${el.score.toFixed(4)}</span>` : ''}
        </div>
        ${el.selector ? `<div style="font-size: 11px; color: #64748b; margin-bottom: 6px;">Selector: <code style="background: #1e293b; padding: 2px 6px; border-radius: 4px;">${escapeHtml(el.selector)}</code></div>` : ''}
        <pre style="background: #0f172a; border: 1px solid #334155; border-radius: 8px; padding: 12px; overflow-x: auto; font-size: 11px; color: #e2e8f0; font-family: 'JetBrains Mono', monospace; margin: 0;"><code>${escapeHtml(el.snippet)}</code></pre>
      </div>`;
      });
    }

    // Deep CLS elements with snippets - use extendedHTML for full code like popup modal
    if (deepCLSResults?.elements?.length > 0) {
      deepCLSResults.elements.filter(el => el.extendedHTML || el.node || el.snippet || el.html).slice(0, 5).forEach((el, i) => {
        const code = el.extendedHTML || el.node || el.snippet || el.html;
        if (!code) return;
        html += `
      <div style="margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 12px; font-weight: 600; color: #a855f7;">${lang === 'cs' ? 'Posun layoutu' : 'Layout Shift'} ${i + 1}</span>
          ${el.cause ? `<span style="font-size: 10px; padding: 2px 8px; background: #a855f720; color: #a855f7; border-radius: 12px;">${escapeHtml(el.cause)}</span>` : ''}
        </div>
        ${el.selector ? `<div style="font-size: 11px; color: #64748b; margin-bottom: 6px;">Selector: <code style="background: #1e293b; padding: 2px 6px; border-radius: 4px;">${escapeHtml(el.selector)}</code></div>` : ''}
        <pre style="background: #0f172a; border: 1px solid #334155; border-radius: 8px; padding: 12px; overflow-x: auto; max-height: 400px; font-size: 11px; color: #e2e8f0; font-family: 'JetBrains Mono', monospace; margin: 0;"><code>${escapeHtml(code)}</code></pre>
      </div>`;
      });
    }

    // INP Elements
    if (inpDiag?.elements?.length > 0) {
      inpDiag.elements.filter(el => el.node || el.snippet).slice(0, 3).forEach((el, i) => {
        const code = el.node || el.snippet;
        html += `
      <div style="margin-bottom: 20px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 12px; font-weight: 600; color: #ef4444;">INP Element ${i + 1}</span>
          ${el.interactionType ? `<span style="font-size: 10px; padding: 2px 8px; background: #ef444420; color: #ef4444; border-radius: 12px;">${escapeHtml(el.interactionType)}</span>` : ''}
        </div>
        ${el.selector ? `<div style="font-size: 11px; color: #64748b; margin-bottom: 6px;">Selector: <code style="background: #1e293b; padding: 2px 6px; border-radius: 4px;">${escapeHtml(el.selector)}</code></div>` : ''}
        <pre style="background: #0f172a; border: 1px solid #334155; border-radius: 8px; padding: 12px; overflow-x: auto; font-size: 11px; color: #e2e8f0; font-family: 'JetBrains Mono', monospace; margin: 0;"><code>${escapeHtml(code)}</code></pre>
      </div>`;
      });
    }

    html += `
    </div>`;
  }

  // === SECTION: LCP Issues with Details ===
  if (lcpDiag?.issues?.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">⚠️ ${lang === 'cs' ? 'LCP problémy' : 'LCP Issues'}</div>`;

    lcpDiag.issues.forEach(issue => {
      const severityColor = issue.severity === 'high' ? '#ef4444' : issue.severity === 'medium' ? '#f59e0b' : '#3b82f6';
      html += `
      <div style="padding: 12px; background: ${severityColor}10; border: 1px solid ${severityColor}30; border-radius: 8px; margin-bottom: 10px;">
        <div style="font-size: 13px; font-weight: 600; color: ${severityColor}; margin-bottom: 4px;">${escapeHtml(issue.message || issue.type)}</div>
        ${issue.items?.length > 0 ? `
        <div style="margin-top: 8px;">
          ${issue.items.slice(0, 3).map(item => `
          <div style="font-size: 11px; color: #94a3b8; padding: 4px 0;">
            ${item.url ? `<code style="background: #1e293b; padding: 2px 6px; border-radius: 4px; word-break: break-all;">${escapeHtml(String(item.url).slice(0, 80))}</code>` : ''}
            ${item.wastedMs ? `<span style="color: #ef4444; margin-left: 8px;">-${Math.round(item.wastedMs)}ms</span>` : ''}
          </div>`).join('')}
        </div>` : ''}
      </div>`;
    });

    html += `
    </div>`;
  }

  // === SECTION: CLS Issues with Details ===
  if (clsDiag?.issues?.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">⚠️ ${lang === 'cs' ? 'CLS problémy' : 'CLS Issues'}</div>`;

    clsDiag.issues.forEach(issue => {
      const severityColor = issue.severity === 'high' ? '#ef4444' : issue.severity === 'medium' ? '#f59e0b' : '#3b82f6';
      html += `
      <div style="padding: 12px; background: ${severityColor}10; border: 1px solid ${severityColor}30; border-radius: 8px; margin-bottom: 10px;">
        <div style="font-size: 13px; font-weight: 600; color: ${severityColor}; margin-bottom: 4px;">${escapeHtml(issue.message || issue.messageEn || issue.type)}</div>
        ${issue.count ? `<div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'Počet' : 'Count'}: ${issue.count}</div>` : ''}
        ${issue.items?.length > 0 ? `
        <div style="margin-top: 8px;">
          ${issue.items.slice(0, 3).map(item => `
          <div style="font-size: 11px; color: #94a3b8; padding: 4px 0;">
            ${item.node ? `<code style="background: #1e293b; padding: 2px 6px; border-radius: 4px;">${escapeHtml(String(item.node).slice(0, 100))}</code>` : ''}
            ${item.selector ? `<code style="background: #1e293b; padding: 2px 6px; border-radius: 4px;">${escapeHtml(item.selector)}</code>` : ''}
          </div>`).join('')}
        </div>` : ''}
      </div>`;
    });

    html += `
    </div>`;
  }

  if (deepCLSResults?.recommendations?.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">💡 ${lang === 'cs' ? 'Doporučení' : 'Recommendations'}</div>
      <ul class="recommendations-list">`;

    deepCLSResults.recommendations.forEach(rec => {
      if (typeof rec === 'string') {
        html += `<li>${escapeHtml(rec)}</li>`;
      } else if (rec && typeof rec === 'object') {
        const title = rec.title?.[lang] || rec.title?.en || rec.title || '';
        const description = rec.description?.[lang] || rec.description?.en || rec.description || '';
        const fixSteps = rec.fix?.[lang] || rec.fix?.en || rec.fix || [];

        html += `<li style="margin-bottom: 12px;">`;
        if (title) {
          html += `<strong>${escapeHtml(typeof title === 'string' ? title : '')}</strong>`;
        }
        if (description) {
          html += `<div style="margin-top: 4px; opacity: 0.9;">${escapeHtml(typeof description === 'string' ? description : '')}</div>`;
        }
        if (Array.isArray(fixSteps) && fixSteps.length > 0) {
          html += `<ul style="margin-top: 6px; margin-left: 16px; list-style: disc;">`;
          fixSteps.forEach(step => {
            html += `<li style="margin: 2px 0;">${escapeHtml(step)}</li>`;
          });
          html += `</ul>`;
        }
        html += `</li>`;
      }
    });

    html += `
      </ul>
    </div>`;
  }

  if (tasks && tasks.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">✅ ${lang === 'cs' ? 'Úkoly pro vývojáře' : 'Developer Tasks'}</div>`;

    tasks.forEach(task => {
      html += `
      <div class="task-card ${escapeHtml(task.priority)}">
        <span class="task-priority ${escapeHtml(task.priority)}">${escapeHtml(priorityLabels[lang]?.[task.priority] || task.priority)}</span>
        <div class="task-title">${escapeHtml(task.title)}</div>
        <div class="task-description">${escapeHtml(task.description)}</div>
        ${task.selector ? `<div class="element-selector" style="margin-bottom: 12px;">${escapeHtml(task.selector)}</div>` : ''}
        <div class="task-fix">${escapeHtml(task.fix)}</div>
      </div>`;
    });

    html += `
    </div>`;
  }

  if (deepCLSResults?.domMutations?.length > 0) {
    const mutations = deepCLSResults.domMutations;
    html += `
    <div class="section">
      <div class="section-title">🔄 ${lang === 'cs' ? 'Dynamický obsah' : 'Dynamic Content'}</div>
      <p style="font-size: 12px; color: #94a3b8; margin-bottom: 12px;">${lang === 'cs' ? 'DOM mutace způsobující layout shifty:' : 'DOM mutations causing layout shifts:'}</p>
      ${mutations.slice(0, 8).map(m => `
        <div style="display: flex; justify-content: space-between; padding: 8px 12px; background: #1e293b; border-radius: 6px; margin-bottom: 6px;">
          <div style="display: flex; gap: 8px; align-items: center;">
            <span style="font-size: 10px; padding: 2px 6px; background: ${m.type === 'add' ? '#22c55e' : '#ef4444'}33; color: ${m.type === 'add' ? '#22c55e' : '#ef4444'}; border-radius: 4px;">${m.type === 'add' ? '+' : '-'}</span>
            <span style="font-size: 11px; color: #e2e8f0; max-width: 250px; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(m.selector || m.target || 'Unknown')}</span>
          </div>
          <span style="font-size: 11px; color: #f59e0b;">${m.timing ? Math.round(m.timing) + 'ms' : ''}</span>
        </div>
      `).join('')}
    </div>`;
  }

  if (multiPageResults) {
    const { summary, pages } = multiPageResults;

    html += `
    <div class="section">
      <div class="section-title">📊 ${lang === 'cs' ? 'Multi-page CLS analýza' : 'Multi-page CLS Analysis'}</div>

      <div class="impact-grid">
        <div class="impact-card">
          <div class="impact-label" style="color: #22c55e;">${lang === 'cs' ? 'Dobré' : 'Good'}</div>
          <div style="font-size: 24px; font-weight: 700; color: #22c55e;">${summary?.goodPages || 0}</div>
        </div>
        <div class="impact-card">
          <div class="impact-label" style="color: #f59e0b;">${lang === 'cs' ? 'K zlepšení' : 'Needs Improvement'}</div>
          <div style="font-size: 24px; font-weight: 700; color: #f59e0b;">${summary?.needsImprovementPages || 0}</div>
        </div>
        <div class="impact-card">
          <div class="impact-label" style="color: #ef4444;">${lang === 'cs' ? 'Špatné' : 'Poor'}</div>
          <div style="font-size: 24px; font-weight: 700; color: #ef4444;">${summary?.poorPages || 0}</div>
        </div>
      </div>

      ${pages && pages.length > 0 ? `
      <div style="margin-top: 16px;">
        ${pages.slice(0, 10).map(page => `
          <div style="margin-bottom: 12px; padding: 12px; background: ${page.cls <= 0.1 ? 'rgba(34, 197, 94, 0.1)' : page.cls <= 0.25 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; border-radius: 8px; border: 1px solid ${page.cls <= 0.1 ? 'rgba(34, 197, 94, 0.3)' : page.cls <= 0.25 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'};">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 12px; color: #e2e8f0; max-width: 70%; overflow: hidden; text-overflow: ellipsis; font-family: monospace;">${escapeHtml(page.url)}</span>
              <span style="font-size: 14px; font-weight: 700; color: ${page.cls <= 0.1 ? '#22c55e' : page.cls <= 0.25 ? '#f59e0b' : '#ef4444'};">${page.cls?.toFixed(3)}</span>
            </div>
            ${page.causes && page.causes.length > 0 ? `
              <div style="margin-top: 8px; padding-left: 12px; border-left: 2px solid #475569;">
                ${page.causes.map(causeItem => `
                  <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px;">
                    <span style="color: #94a3b8;">├─ ${escapeHtml(causeItem.cause)}</span>
                    <span style="color: #64748b;">${causeItem.count}×</span>
                  </div>
                `).join('')}
                ${page.topElement ? `
                  <div style="padding: 4px 0; font-size: 10px; margin-top: 4px; border-top: 1px solid #334155;">
                    <span style="color: #64748b;">└─ </span>
                    <code style="color: #a78bfa; font-family: monospace;">${escapeHtml(page.topElement.length > 60 ? page.topElement.slice(0, 60) + '...' : page.topElement)}</code>
                  </div>
                ` : ''}
              </div>
            ` : page.topCause ? `
              <div style="margin-top: 6px; font-size: 11px; color: #94a3b8;">
                ${lang === 'cs' ? 'Hlavní příčina' : 'Main cause'}: ${escapeHtml(page.topCause)}
              </div>
            ` : ''}
          </div>
        `).join('')}
      </div>
      ` : ''}
    </div>`;
  }

  const metricData = diagnostics?.[metric?.toLowerCase()];

  const causesFromApi = metricData?.causes || [];
  const causesFromDeep = [];

  if (deepCLSResults?.elements?.length > 0) {
    const causeCounts = {};
    const causeItems = {};
    deepCLSResults.elements.forEach(el => {
      const cause = el.cause || 'dynamic-content';
      causeCounts[cause] = (causeCounts[cause] || 0) + 1;
      if (!causeItems[cause]) causeItems[cause] = [];
      if (el.selector) causeItems[cause].push(el.selector);
    });
    Object.entries(causeCounts).forEach(([type, count]) => {
      causesFromDeep.push({
        type,
        count,
        severity: count > 3 ? 'high' : count > 1 ? 'medium' : 'low',
        items: causeItems[type] || []
      });
    });
  }

  const allCauses = causesFromApi.length > 0 ? causesFromApi : causesFromDeep;

  const causeTitleLabels = {
    cs: {
      'unsized-images': 'Obrázky bez rozměrů',
      'unsized-image': 'Obrázky bez rozměrů',
      'embeds': 'Vložené prvky',
      'embed': 'Vložené prvky',
      'iframe-embed': 'Vložené prvky (iframe)',
      'video-embed': 'Vložené prvky (video)',
      'dynamic-content': 'Dynamický obsah',
      'font-swap': 'Web fonty (FOUT)',
      'lazy-content': 'Lazy-loaded obsah',
      'ad-injection': 'Reklamy',
      'consent-banner': 'Cookie consent'
    },
    en: {
      'unsized-images': 'Unsized images',
      'unsized-image': 'Unsized images',
      'embeds': 'Embedded elements',
      'embed': 'Embedded elements',
      'iframe-embed': 'Embedded elements (iframe)',
      'video-embed': 'Embedded elements (video)',
      'dynamic-content': 'Dynamic content',
      'font-swap': 'Web fonts (FOUT)',
      'lazy-content': 'Lazy-loaded content',
      'ad-injection': 'Ads',
      'consent-banner': 'Cookie consent'
    }
  };

  const causeDescLabels = {
    cs: {
      'unsized-images': 'Obrázky bez atributů width/height způsobují posun layoutu při načtení.',
      'unsized-image': 'Obrázky bez atributů width/height způsobují posun layoutu při načtení.',
      'embeds': 'Vložené prvky (iframe, video) bez rezervovaného místa.',
      'embed': 'Vložené prvky (iframe, video) bez rezervovaného místa.',
      'iframe-embed': 'Iframe bez rezervovaného místa způsobuje posun layoutu.',
      'video-embed': 'Video bez rezervovaného místa způsobuje posun layoutu.',
      'dynamic-content': 'Dynamicky vkládaný obsah způsobuje layout shifty.',
      'font-swap': 'Web fonty způsobují FOUT (Flash of Unstyled Text).',
      'lazy-content': 'Lazy-loaded obsah bez rezervovaného místa.',
      'ad-injection': 'Dynamicky vkládané reklamy způsobují layout shifty.',
      'consent-banner': 'Cookie consent banner způsobuje posun obsahu.'
    },
    en: {
      'unsized-images': 'Images without width/height attributes cause layout shift on load.',
      'unsized-image': 'Images without width/height attributes cause layout shift on load.',
      'embeds': 'Embedded elements (iframe, video) without reserved space.',
      'embed': 'Embedded elements (iframe, video) without reserved space.',
      'iframe-embed': 'Iframe without reserved space causes layout shift.',
      'video-embed': 'Video without reserved space causes layout shift.',
      'dynamic-content': 'Dynamically injected content causes layout shifts.',
      'font-swap': 'Web fonts cause FOUT (Flash of Unstyled Text).',
      'lazy-content': 'Lazy-loaded content without reserved space.',
      'ad-injection': 'Dynamically injected ads cause layout shifts.',
      'consent-banner': 'Cookie consent banner causes content shift.'
    }
  };

  const causeRecLabels = {
    cs: {
      'unsized-images': ['Přidejte width a height atributy ke všem obrázkům', 'Použijte aspect-ratio CSS property'],
      'unsized-image': ['Přidejte width a height atributy ke všem obrázkům', 'Použijte aspect-ratio CSS property'],
      'embeds': ['Rezervujte místo pomocí aspect-ratio kontejneru', 'Lazy-loadujte embedy pod foldem'],
      'embed': ['Rezervujte místo pomocí aspect-ratio kontejneru', 'Lazy-loadujte embedy pod foldem'],
      'iframe-embed': ['Rezervujte místo pomocí aspect-ratio kontejneru', 'Lazy-loadujte iframe pod foldem'],
      'video-embed': ['Rezervujte místo pomocí aspect-ratio kontejneru', 'Použijte poster atribut pro video'],
      'dynamic-content': ['Použijte skeleton loadery pro async obsah', 'Rezervujte místo pro lazy-loaded komponenty'],
      'font-swap': ['Použijte font-display: swap', 'Preloadujte kritické fonty'],
      'lazy-content': ['Definujte min-height pro lazy kontejnery', 'Použijte skeleton loading'],
      'ad-injection': ['Rezervujte místo pro reklamy', 'Načítejte reklamy po foldu'],
      'consent-banner': ['Umístěte banner do fixní pozice', 'Nezpůsobujte posun hlavního obsahu']
    },
    en: {
      'unsized-images': ['Add width and height attributes to all images', 'Use aspect-ratio CSS property'],
      'unsized-image': ['Add width and height attributes to all images', 'Use aspect-ratio CSS property'],
      'embeds': ['Reserve space with aspect-ratio container', 'Lazy-load embeds below the fold'],
      'embed': ['Reserve space with aspect-ratio container', 'Lazy-load embeds below the fold'],
      'iframe-embed': ['Reserve space with aspect-ratio container', 'Lazy-load iframe below the fold'],
      'video-embed': ['Reserve space with aspect-ratio container', 'Use poster attribute for video'],
      'dynamic-content': ['Use skeleton loaders for async content', 'Reserve space for lazy-loaded components'],
      'font-swap': ['Use font-display: swap', 'Preload critical fonts'],
      'lazy-content': ['Define min-height for lazy containers', 'Use skeleton loading'],
      'ad-injection': ['Reserve space for ads', 'Load ads below the fold'],
      'consent-banner': ['Place banner in fixed position', 'Do not shift main content']
    }
  };

  const causeCodeExamples = {
    'unsized-images': {
      bad: '<img src="foto.jpg" alt="Foto">',
      good: '<img src="foto.jpg" alt="Foto" width="800" height="600">'
    },
    'unsized-image': {
      bad: '<img src="foto.jpg" alt="Foto">',
      good: '<img src="foto.jpg" alt="Foto" width="800" height="600">'
    },
    'embeds': {
      bad: '<iframe src="..."></iframe>',
      good: '<div style="aspect-ratio: 16/9;"><iframe src="..." style="width:100%; height:100%;"></iframe></div>'
    },
    'embed': {
      bad: '<iframe src="..."></iframe>',
      good: '<div style="aspect-ratio: 16/9;"><iframe src="..." style="width:100%; height:100%;"></iframe></div>'
    },
    'iframe-embed': {
      bad: '<iframe src="..."></iframe>',
      good: '<div style="aspect-ratio: 16/9;"><iframe src="..." style="width:100%; height:100%;"></iframe></div>'
    },
    'video-embed': {
      bad: '<video src="..."></video>',
      good: '<video src="..." poster="poster.jpg" width="640" height="360"></video>'
    },
    'font-swap': {
      bad: "@font-face { font-family: 'MyFont'; src: url(...); }",
      good: "@font-face { font-family: 'MyFont'; src: url(...); font-display: swap; }"
    },
    'dynamic-content': {
      bad: '<div id="widget"></div>',
      good: '<div id="widget" style="min-height: 200px;"></div>'
    },
    'lazy-content': {
      bad: '<div class="lazy-section"></div>',
      good: '<div class="lazy-section skeleton" style="min-height: 300px;"></div>'
    },
    'ad-injection': {
      bad: '<div class="ad-slot"></div>',
      good: '<div class="ad-slot" style="min-height: 250px; aspect-ratio: 300/250;"></div>'
    }
  };

  if (allCauses.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">🎯 ${lang === 'cs' ? 'Detekované příčiny' : 'Detected Causes'}</div>`;

    allCauses.forEach(cause => {
      const severity = cause.severity || 'medium';
      const severityColor = severity === 'high' ? '#ef4444' : severity === 'medium' ? '#f59e0b' : '#3b82f6';
      const severityBg = severity === 'high' ? 'rgba(239, 68, 68, 0.1)' : severity === 'medium' ? 'rgba(249, 115, 22, 0.1)' : 'rgba(59, 130, 246, 0.1)';
      const severityLabel = severity === 'high' ? (lang === 'cs' ? 'vysoká' : 'high') : severity === 'medium' ? (lang === 'cs' ? 'střední' : 'medium') : (lang === 'cs' ? 'nízká' : 'low');
      const title = escapeHtml(causeTitleLabels[lang]?.[cause.type] || cause.type || '');
      const desc = escapeHtml(causeDescLabels[lang]?.[cause.type] || '');
      const recs = causeRecLabels[lang]?.[cause.type] || [];
      const codeExample = causeCodeExamples[cause.type];
      const countText = cause.count > 1
        ? (lang === 'cs' ? `(${cause.count}× posun)` : `(${cause.count}× shift)`)
        : '';

      html += `
      <div style="padding: 16px; background: ${severityBg}; border: 1px solid ${severityColor}30; border-radius: 12px; margin-bottom: 16px;">
        <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 14px; font-weight: 600; color: ${severityColor};">${title}</span>
          <span style="font-size: 10px; padding: 2px 8px; background: ${severityColor}30; color: ${severityColor}; border-radius: 12px;">${severityLabel}</span>
          ${countText ? `<span style="font-size: 11px; color: #94a3b8;">${countText}</span>` : ''}
        </div>
        <div style="font-size: 12px; color: #cbd5e1; margin-bottom: 12px;">
          ${desc}
          ${cause.count > 1 ? `<span style="color: #94a3b8;"> (${lang === 'cs'
            ? `Tento typ problému způsobil ${cause.count} posunutí obsahu během načítání stránky.`
            : `This issue caused ${cause.count} content shifts during page load.`})</span>` : ''}
        </div>
        ${cause.items?.length > 0 ? `
        <div style="margin-bottom: 12px;">
          ${cause.items.slice(0, 3).map(url => `
            <code style="display: block; font-size: 11px; color: #94a3b8; background: #0f172a; padding: 6px 10px; border-radius: 4px; margin-bottom: 4px; word-break: break-all;">${escapeHtml(typeof url === 'string' ? url.replace(/^https?:\/\//, '').slice(0, 70) : String(url || ''))}</code>
          `).join('')}
        </div>
        ` : ''}
        ${recs.length > 0 ? `
        <div style="font-size: 11px; color: #94a3b8; margin-bottom: 12px;">
          <span style="font-weight: 600;">${lang === 'cs' ? 'Doporučení:' : 'Recommendations:'}</span>
          <ul style="margin: 4px 0 0 16px; padding: 0;">
            ${recs.map(rec => `<li>${escapeHtml(rec)}</li>`).join('')}
          </ul>
        </div>
        ` : ''}
        ${codeExample ? `
        <div style="margin-top: 12px;">
          <div style="font-size: 11px; font-weight: 600; color: #94a3b8; margin-bottom: 8px;">${lang === 'cs' ? 'Jak opravit:' : 'How to fix:'}</div>
          <div style="font-size: 10px; color: #94a3b8; line-height: 1.5; margin-bottom: 8px;">${escapeHtml(genericCodeExampleLabel[lang])}</div>
          <div style="display: grid; gap: 8px;">
            <div style="padding: 10px; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.2); border-radius: 6px;">
              <div style="font-size: 10px; color: #f87171; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">✗ ${lang === 'cs' ? 'Obecný problémový vzor' : 'Generic problem pattern'}</div>
              <code style="font-size: 11px; color: #fca5a5; word-break: break-all;">${escapeHtml(codeExample.bad)}</code>
            </div>
            <div style="padding: 10px; background: rgba(34, 197, 94, 0.1); border: 1px solid rgba(34, 197, 94, 0.2); border-radius: 6px;">
              <div style="font-size: 10px; color: #4ade80; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">✓ ${lang === 'cs' ? 'Obecný opravený vzor' : 'Generic fixed pattern'}</div>
              <code style="font-size: 11px; color: #86efac; word-break: break-all;">${escapeHtml(codeExample.good)}</code>
            </div>
          </div>
        </div>
        ` : ''}
      </div>`;
    });

    html += `
    </div>`;
  }

  if (metricData?.issues?.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">⚠️ ${lang === 'cs' ? 'Zjištěné problémy' : 'Detected Issues'}</div>`;

    metricData.issues.forEach(issue => {
      const severity = issue.severity || 'medium';
      const severityColor = severity === 'high' ? '#ef4444' : severity === 'medium' ? '#f59e0b' : '#3b82f6';
      html += `
      <div style="padding: 12px; background: #1e293b; border-left: 3px solid ${severityColor}; margin-bottom: 8px; border-radius: 4px;">
        <div style="font-size: 13px; color: #e2e8f0;">${escapeHtml(issue.message || '')}</div>
        ${issue.details ? `<div style="font-size: 11px; color: #94a3b8; margin-top: 6px;">${escapeHtml(issue.details)}</div>` : ''}
        ${(() => {
          if (!issue.items?.length) return '';
          const itemTexts = issue.items.slice(0, 5).map(item => {
            if (typeof item === 'string') return item;
            const raw = item?.url || item?.script || item?.name || item?.selector || item?.source || item?.text || '';
            if (!raw) return '';
            const cleaned = String(raw).replace(/^https?:\/\//, '');
            return cleaned.length > 70 ? cleaned.slice(0, 70) + '…' : cleaned;
          }).filter(Boolean);
          if (!itemTexts.length) return '';
          return `
        <div style="margin-top: 8px;">
          ${itemTexts.map(text => `
            <div style="font-size: 11px; color: #94a3b8; display: flex; align-items: center; gap: 4px;">
              <span style="color: #22d3ee;">•</span>
              <span style="word-break: break-all;">${escapeHtml(text)}</span>
            </div>
          `).join('')}
        </div>`;
        })()}
      </div>`;
    });

    html += `
    </div>`;
  }

  if (diagnostics?.renderBlocking?.items?.length > 0) {
    html += `
    <div class="section">
      <div class="section-title">🚫 ${lang === 'cs' ? 'Render-blocking zdroje' : 'Render-blocking Resources'}</div>`;

    diagnostics.renderBlocking.items.slice(0, 5).forEach(item => {
      html += `
      <div style="display: flex; justify-content: space-between; padding: 8px 12px; background: #1e293b; border-radius: 4px; margin-bottom: 6px;">
        <span style="font-size: 12px; color: #94a3b8; max-width: 60%; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(item.url || '')}</span>
        <span style="font-size: 12px; color: #f59e0b;">-${Math.round(item.wastedMs || 0)}ms</span>
      </div>`;
    });

    html += `
    </div>`;
  }

  if (diagnostics?.serverTiming) {
    const st = diagnostics.serverTiming;
    const bd = st.breakdown || {};
    const stTtfb = Math.round(bd.waiting || st.ttfb || 0);
    html += `
    <div class="section w3c-section">
      <div class="section-title">⏱️ Server Timing <span class="w3c-badge">W3C</span></div>
      ${stTtfb <= 0 ? `<div style="font-size: 12px; color: #94a3b8;">${lang === 'cs'
        ? 'Data z Navigation Timing nejsou pro tento běh dostupná (naměřené hodnoty jsou nulové) — sekci nelze interpretovat.'
        : 'Navigation Timing data is not available for this run (measured values are zero) — this section cannot be interpreted.'}</div>` : `
      <div class="timing-grid">
        ${[
          { label: 'DNS', v: bd.dns ?? st.dns },
          { label: 'TCP', v: bd.connection ?? st.tcp },
          { label: 'TLS', v: bd.tls ?? st.tls },
          { label: 'TTFB', v: bd.waiting ?? st.ttfb },
          { label: 'DL', v: bd.receiving ?? st.download }
        ].map(p => `
        <div class="timing-item">
          <div class="timing-value">${(p.v == null || (bd.phasesAvailable === false && p.v === 0 && p.label !== 'TTFB' && p.label !== 'DL')) ? '—' : Math.round(p.v) + 'ms'}</div>
          <div class="timing-label">${p.label}</div>
        </div>`).join('')}
      </div>
      ${bd.phasesAvailable === false ? `<div style="margin-top: 8px; font-size: 10px; color: #64748b; text-align: center;">${lang === 'cs'
        ? 'Fáze DNS / TCP / TLS nejsou z Lighthouse dostupné — „—" znamená neměřeno, ne 0 ms. TTFB zde měří jen serverovou část (bez DNS/TCP/TLS/redirectů), takže bývá nižší než field TTFB z CrUX.'
        : 'DNS / TCP / TLS phases are not available from Lighthouse — "—" means not measured, not 0 ms. TTFB here covers the server portion only (no DNS/TCP/TLS/redirects), so it tends to be lower than field TTFB from CrUX.'}</div>` : ''}
      ${stTtfb >= 10 && stTtfb < 200 ? `<div style="margin-top: 12px; font-size: 12px; color: #22c55e; text-align: center;">${lang === 'cs' ? '✓ Rychlá odpověď serveru (TTFB ' : '✓ Fast server response (TTFB '}${stTtfb}ms)</div>` : ''}
      ${stTtfb > 0 && stTtfb < 10 ? `<div style="margin-top: 12px; font-size: 11px; color: #f59e0b; text-align: center;">${lang === 'cs'
        ? `TTFB ${stTtfb} ms je nerealisticky nízké — dokument byl pravděpodobně obsloužen z cache, o rychlosti serveru to nic neříká.`
        : `TTFB of ${stTtfb} ms is unrealistically low — the document was likely served from cache; it says nothing about server speed.`}</div>` : ''}`}
    </div>`;
  }

  if (diagnostics?.longTasks?.count > 0) {
    const lt = diagnostics.longTasks;
    const formatMs = (ms) => Math.round(ms || 0);
    const tasksList = lt.tasks || lt.items || [];
    const avgDuration = lt.avgDuration || (tasksList.length > 0
      ? tasksList.reduce((sum, t) => sum + (t.duration || 0), 0) / tasksList.length
      : 0);
    html += `
    <div class="section">
      <div class="section-title">⏳ ${lang === 'cs' ? 'Dlouhé úlohy (>50ms) — celé načtení stránky' : 'Long Tasks (>50ms) — full page load'} <span class="w3c-badge">W3C</span></div>
      <p>${lang === 'cs'
        ? 'TBT je samostatná metrika omezená na okno Lighthouse mezi FCP a TTI, nikoli součet všech níže vypsaných přesahů za celý záznam.'
        : 'TBT is a separate metric limited to Lighthouse’s FCP–TTI window, not the sum of all listed excesses across the recording.'}</p>
      ${metricLowerKey === 'inp' && !validDeepInp ? `<div style="font-size:12px;color:#fbbf24;line-height:1.6;margin-bottom:12px;">${lang === 'cs'
        ? 'Platné interakční okno v tomto reportu chybí. Tyto Lighthouse úlohy popisují celé načtení stránky; nelze je spojovat s konkrétní interakcí ani vydávat za příčinu field INP.'
        : 'This report has no valid interaction window. These Lighthouse tasks describe the full page load; they cannot be linked to a specific interaction or presented as the cause of field INP.'}</div>` : ''}
      ${diagnostics?.labStability?.tbt && diagnostics.labStability.tbt.min !== diagnostics.labStability.tbt.max ? `
      <div style="font-size: 11px; color: #94a3b8; margin-bottom: 10px;">
        ${lang === 'cs'
          ? `Medián z ${diagnostics.labStability.runs} běhů · TBT ${diagnostics.labStability.tbt.min}–${diagnostics.labStability.tbt.max} ms · počet úloh ${diagnostics.labStability.longTaskCount?.min ?? '—'}–${diagnostics.labStability.longTaskCount?.max ?? '—'}`
          : `Median of ${diagnostics.labStability.runs} runs · TBT ${diagnostics.labStability.tbt.min}–${diagnostics.labStability.tbt.max} ms · task count ${diagnostics.labStability.longTaskCount?.min ?? '—'}–${diagnostics.labStability.longTaskCount?.max ?? '—'}`}
      </div>` : ''}
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px;">
        <div style="background: #1e293b; padding: 16px; border-radius: 8px; text-align: center;">
          <div style="font-size: 20px; font-weight: 700; color: #f59e0b;">${lt.count}×</div>
          <div style="font-size: 11px; color: #64748b;">${lang === 'cs' ? 'Úloh' : 'Tasks'}</div>
        </div>
        <div style="background: #1e293b; padding: 16px; border-radius: 8px; text-align: center;">
          <div style="font-size: 20px; font-weight: 700; color: #ef4444;">${formatMs(lt.totalBlockingTime)}ms</div>
          <div style="font-size: 11px; color: #64748b;">Total Blocking Time</div>
        </div>
        <div style="background: #1e293b; padding: 16px; border-radius: 8px; text-align: center;">
          <div style="font-size: 20px; font-weight: 700; color: #a855f7;">${formatMs(avgDuration)}ms</div>
          <div style="font-size: 11px; color: #64748b;">${lang === 'cs' ? 'Průměrné zpoždění' : 'Avg delay'}</div>
        </div>
      </div>
      ${tasksList.length > 0 ? `
      <div style="space-y: 6px;">
        ${tasksList.slice(0, 5).map(task => {
          const name = task.url ? task.url.split('/').pop()?.slice(0, 40) || 'script' : 'Unattributable';
          const blocking = task.blockingTime || ((task.duration || 0) - 50);
          return `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #1e293b; border-radius: 6px; margin-bottom: 6px;">
            <span style="font-size: 12px; color: #e2e8f0; max-width: 50%; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(name)}</span>
            <div style="display: flex; gap: 12px;">
              <span style="font-size: 12px; color: #94a3b8;">${formatMs(task.duration)}ms</span>
              <span style="font-size: 12px; color: #ef4444;">+${formatMs(blocking)}ms block</span>
            </div>
          </div>`;
        }).join('')}
      </div>
      <div style="margin-top: 12px; font-size: 11px; color: #64748b;">
        ${(() => {
          const rec = diagnostics?.longTasks?.impactOnINP?.recommendation;
          if (rec) return `${lang === 'cs' ? 'Doporučení' : 'Recommendation'}: ${escapeHtml(rec)}`;
          const tpTasks = tasksList.filter(t => t.isThirdParty).length;
          const mostlyThirdParty = tasksList.length > 0 && tpTasks / tasksList.length > 0.5;
          return mostlyThirdParty
            ? (lang === 'cs' ? 'Doporučení: Většinu úloh způsobují skripty třetích stran — scheduler.yield() v cizím kódu nepoužijete. Řešte správu tagů (odložení, server-side tagging, odstranění).' : 'Recommendation: Most tasks come from third-party scripts — you cannot add scheduler.yield() to foreign code. Address tag governance (deferral, server-side tagging, removal).')
            : (lang === 'cs' ? 'Doporučení: Rozdělit dlouhé úlohy ve vlastním kódu pomocí scheduler.yield() nebo requestIdleCallback()' : 'Recommendation: Split long tasks in your own code using scheduler.yield() or requestIdleCallback()');
        })()}
      </div>
      ` : ''}
    </div>`;
  }

  if (includeActionPlan && deepCLSResults) {
    const cls = deepCLSResults.cls || 0;
    const elements = deepCLSResults.elements || [];
    const isCritical = cls > 0.25;
    const needsWork = cls > 0.1;

    // Find the main culprit element
    const sortedElements = [...elements].sort((a, b) => (b.totalShift || 0) - (a.totalShift || 0));
    const mainCulprit = sortedElements[0];
    const totalShift = elements.reduce((sum, el) => sum + (el.totalShift || 0), 0);
    const mainCulpritPercent = mainCulprit && totalShift > 0
      ? Math.round((mainCulprit.totalShift || 0) / totalShift * 100)
      : 0;

    html += `
    <div class="section action-plan-section">
      <div class="section-title">📋 ${lang === 'cs' ? 'Akční plán' : 'Action Plan'}</div>

      <!-- Client Summary -->
      <div class="client-summary">
        <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; margin-bottom: 12px;">${lang === 'cs' ? 'Shrnutí pro klienta' : 'Client Summary'}</div>
        <div style="font-size: 15px; color: #e2e8f0; line-height: 1.7; margin-bottom: 16px;">
          ${isCritical
            ? (lang === 'cs'
              ? `Web má vážný problém se stabilitou layoutu (CLS: ${cls.toFixed(3)}). Obsah stránky "poskakuje" při načítání nebo scrollování, což frustruje uživatele a negativně ovlivňuje pozici ve vyhledávačích.`
              : `The website has a serious layout stability issue (CLS: ${cls.toFixed(3)}). Page content "jumps" during loading or scrolling, which frustrates users and negatively affects search rankings.`)
            : needsWork
            ? (lang === 'cs'
              ? `Web má mírné problémy se stabilitou layoutu (CLS: ${cls.toFixed(3)}). Doporučujeme optimalizaci pro lepší uživatelský zážitek.`
              : `The website has moderate layout stability issues (CLS: ${cls.toFixed(3)}). Optimization recommended for better user experience.`)
            : (lang === 'cs'
              ? `Stabilita layoutu je v pořádku (CLS: ${cls.toFixed(3)}). Gratulujeme!`
              : `Layout stability is good (CLS: ${cls.toFixed(3)}). Congratulations!`)
          }
        </div>
        ${mainCulprit ? `
        <div style="font-size: 14px; color: #f59e0b; font-weight: 600;">
          ${lang === 'cs' ? 'Hlavní viník' : 'Main culprit'}: element "${escapeHtml(mainCulprit.selector || mainCulprit.tagName || 'unknown')}" ${lang === 'cs' ? 'způsobuje' : 'causes'} ${mainCulpritPercent}% ${lang === 'cs' ? 'všech posunů' : 'of all shifts'}.
        </div>
        ` : ''}
      </div>

      <!-- Impact Cards -->
      <div class="impact-cards">
        <div class="impact-card-detailed seo">
          <div style="font-size: 12px; font-weight: 600; color: #ef4444; margin-bottom: 8px;">SEO</div>
          <div style="font-size: 13px; color: #cbd5e1; line-height: 1.5;">
            ${isCritical
              ? (lang === 'cs' ? 'Vysoký dopad na SEO - Google penalizuje stránky s vysokým CLS' : 'High SEO impact - Google penalizes pages with high CLS')
              : needsWork
              ? (lang === 'cs' ? 'Mírný dopad - CLS je jedním z Core Web Vitals' : 'Moderate impact - CLS is one of Core Web Vitals')
              : (lang === 'cs' ? 'V normě - pozitivní signál pro ranking' : 'OK - positive ranking signal')}
          </div>
        </div>
        <div class="impact-card-detailed ux">
          <div style="font-size: 12px; font-weight: 600; color: #f97316; margin-bottom: 8px;">UX</div>
          <div style="font-size: 13px; color: #cbd5e1; line-height: 1.5;">
            ${isCritical
              ? (lang === 'cs' ? 'Uživatelé mohou kliknout na špatné místo kvůli posunům' : 'Users may misclick due to layout shifts')
              : needsWork
              ? (lang === 'cs' ? 'Občasné posuny mohou zmást uživatele' : 'Occasional shifts may confuse users')
              : (lang === 'cs' ? 'Stabilní layout = spokojení uživatelé' : 'Stable layout = happy users')}
          </div>
        </div>
        <div class="impact-card-detailed conversion">
          <div style="font-size: 12px; font-weight: 600; color: #f59e0b; margin-bottom: 8px;">${lang === 'cs' ? 'Konverze' : 'Conversion'}</div>
          <div style="font-size: 13px; color: #cbd5e1; line-height: 1.5;">
            ${isCritical
              ? (lang === 'cs' ? 'Může snížit konverze o 5-10%' : 'May reduce conversions by 5-10%')
              : needsWork
              ? (lang === 'cs' ? 'Potenciální ztráta 2-5% konverzí' : 'Potential 2-5% conversion loss')
              : (lang === 'cs' ? 'Žádný negativní dopad' : 'No negative impact')}
          </div>
        </div>
      </div>

      <!-- Developer Tasks -->
      ${sortedElements.length > 0 ? `
      <div style="margin-top: 24px;">
        <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
          🛠️ ${lang === 'cs' ? 'Úkoly pro vývojáře' : 'Developer Tasks'}
        </div>

        ${sortedElements.slice(0, 5).map((el, idx) => {
          const shift = el.totalShift || 0;
          const priority = shift > 0.15 ? 'critical' : shift > 0.05 ? 'high' : 'medium';
          const priorityLabel = priority === 'critical'
            ? (lang === 'cs' ? 'Kritické' : 'Critical')
            : priority === 'high'
            ? (lang === 'cs' ? 'Vysoká' : 'High')
            : (lang === 'cs' ? 'Střední' : 'Medium');

          const cause = el.cause || 'dynamic-content';
          const causeLabel = causeLabels[lang]?.[cause] || cause;

          // Generate task title based on cause
          const taskTitles = {
            cs: {
              'consent-banner': 'Opravit cookie consent banner',
              'font-swap': 'Optimalizovat načítání fontů',
              'ad-injection': 'Rezervovat místo pro reklamy',
              'unsized-image': 'Přidat rozměry obrázkům',
              'iframe-embed': 'Rezervovat místo pro iframe',
              'video-embed': 'Rezervovat místo pro video',
              'dynamic-content': 'Opravit dynamický widget',
              'animation': 'Opravit animace',
              'image-load': 'Optimalizovat načítání obrázků'
            },
            en: {
              'consent-banner': 'Fix cookie consent banner',
              'font-swap': 'Optimize font loading',
              'ad-injection': 'Reserve space for ads',
              'unsized-image': 'Add image dimensions',
              'iframe-embed': 'Reserve space for iframe',
              'video-embed': 'Reserve space for video',
              'dynamic-content': 'Fix dynamic widget',
              'animation': 'Fix animations',
              'image-load': 'Optimize image loading'
            }
          };

          const taskTitle = taskTitles[lang]?.[cause] || (lang === 'cs' ? 'Opravit layout shift' : 'Fix layout shift');

          // Generate solution based on cause
          const solutions = {
            cs: {
              'consent-banner': ['Nastavte position: fixed nebo absolute pro banner', 'Použijte transform místo změny velikosti', 'Načítejte banner asynchronně až po DOMContentLoaded'],
              'font-swap': ['Použijte font-display: optional nebo swap', 'Preloadujte kritické fonty pomocí <link rel="preload">', 'Zvažte použití systémových fontů jako fallback'],
              'ad-injection': ['Přidejte min-height kontejneru odpovídající velikosti reklamy', 'Použijte aspect-ratio pro responzivní reklamy', 'Načítejte reklamy lazy loading pod foldem'],
              'unsized-image': ['Přidejte atributy width a height na <img> tag', 'Použijte aspect-ratio v CSS', 'Zvažte použití srcset pro responzivní obrázky'],
              'iframe-embed': ['Přidejte width a height na iframe', 'Obalte iframe do kontejneru s aspect-ratio', 'Použijte lazy loading pro iframes pod foldem'],
              'video-embed': ['Přidejte poster image a rozměry', 'Použijte aspect-ratio: 16/9 kontejner', 'Načítejte video player lazy'],
              'dynamic-content': ['Přidejte min-height nebo aspect-ratio kontejner', 'Pokud jde o reklamu/embed, použijte placeholder', 'Načítejte obsah lazy až pod foldem'],
              'animation': ['Použijte transform a opacity místo width/height', 'Animujte pouze composite properties', 'Vyhněte se animacím měnícím layout'],
              'image-load': ['Nastavte explicitní rozměry všem obrázkům', 'Použijte lazy loading s placeholder', 'Preloadujte above-the-fold obrázky']
            },
            en: {
              'consent-banner': ['Set position: fixed or absolute for the banner', 'Use transform instead of resizing', 'Load banner async after DOMContentLoaded'],
              'font-swap': ['Use font-display: optional or swap', 'Preload critical fonts with <link rel="preload">', 'Consider system fonts as fallback'],
              'ad-injection': ['Add min-height matching the ad size', 'Use aspect-ratio for responsive ads', 'Load ads lazy below the fold'],
              'unsized-image': ['Add width and height attributes to <img>', 'Use aspect-ratio in CSS', 'Consider srcset for responsive images'],
              'iframe-embed': ['Add width and height to iframe', 'Wrap iframe in aspect-ratio container', 'Use lazy loading for iframes below fold'],
              'video-embed': ['Add poster image and dimensions', 'Use aspect-ratio: 16/9 container', 'Load video player lazily'],
              'dynamic-content': ['Add min-height or aspect-ratio container', 'Use placeholder for ads/embeds', 'Load content lazy below the fold'],
              'animation': ['Use transform and opacity instead of width/height', 'Animate only composite properties', 'Avoid animations that change layout'],
              'image-load': ['Set explicit dimensions on all images', 'Use lazy loading with placeholder', 'Preload above-the-fold images']
            }
          };

          const solutionSteps = solutions[lang]?.[cause] || solutions[lang]?.['dynamic-content'] || [];

          return `
        <div class="dev-task ${priority}">
          <span class="dev-task-priority ${priority}">${priorityLabel}</span>
          <div style="font-size: 16px; font-weight: 600; color: #e2e8f0; margin-bottom: 8px;">${taskTitle}</div>
          <div style="font-size: 13px; color: #94a3b8; margin-bottom: 12px;">
            Widget "${escapeHtml(el.selector || el.tagName || 'unknown')}" ${evidenceContract
              ? (lang === 'cs' ? 'je kandidát spojený s pozorovaným posunem; mechanismus vyžaduje ověření' : 'is a candidate associated with the observed shift; the mechanism requires verification')
              : (lang === 'cs' ? 'nemá rezervované místo a způsobuje layout shift' : 'has no reserved space and causes layout shift')} ${shift.toFixed(3)}.
          </div>
          <div class="dev-task-selector">${escapeHtml(el.selector || el.tagName || 'unknown')}</div>
          <div class="dev-task-solution">
            <div class="dev-task-solution-title">${lang === 'cs' ? 'Řešení' : 'Solution'}:</div>
            <ol>
              ${solutionSteps.map(step => `<li>${escapeHtml(step)}</li>`).join('')}
            </ol>
          </div>
        </div>`;
        }).join('')}
      </div>
      ` : ''}

      <div style="margin-top: 20px; padding: 12px; background: #0f172a50; border-radius: 8px; font-size: 11px; color: #64748b; text-align: center;">
        ${lang === 'cs'
          ? 'Tento akční plán byl vygenerován automaticky na základě diagnostiky. Pro detailní implementaci kontaktujte vývojáře.'
          : 'This action plan was generated automatically based on diagnostics. Contact developers for detailed implementation.'}
      </div>
    </div>`;
  }

  if (includeW3C && diagnostics?.resourceTiming) {
    const rt = diagnostics.resourceTiming;
    const formatBytes = (b) => {
      if (!b) return '0 B';
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
      return (b / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const summary = rt.summary || rt.byType || {};
    const totalResources = Object.values(summary).reduce((sum, d) => sum + (d.count || 0), 0);
    const totalSize = Object.values(summary).reduce((sum, d) => sum + (d.size || 0), 0);
    const thirdParty = rt.thirdPartyImpact || {};
    const thirdPartyCount = thirdParty.count || 0;
    const thirdPartySize = thirdParty.totalSize || 0;
    const thirdPartyTime = thirdParty.totalTime || 0;

    html += `
    <div class="section w3c-section">
      <div class="section-title">📊 Resource Timing <span class="w3c-badge">W3C</span></div>
      <p style="font-size: 11px; color: #94a3b8; margin-bottom: 12px;">
        ${lang === 'cs'
          ? 'Přehled všech zdrojů stránky podle typu. 3rd-party zdroje jsou z externích domén a mohou významně ovlivnit rychlost.'
          : 'Overview of all page resources by type. 3rd-party resources are from external domains and can significantly impact speed.'}
      </p>
      ${Object.keys(summary).length > 0 ? `
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px;">
        ${Object.entries(summary).slice(0, 6).map(([type, data]) => {
          const typeIcons = { script: '📜', Script: '📜', font: '🔤', Font: '🔤', image: '🖼️', Image: '🖼️', stylesheet: '🎨', Stylesheet: '🎨', document: '📄', Document: '📄', other: '📦', Other: '📦' };
          // Lighthouse Resource Summary's own 'third-party' category — a
          // different classification than the cross-hostname network-log
          // count below; label the source so the two aren't read as one metric.
          const isTpRow = String(type).toLowerCase() === 'third-party';
          const typeLabel = isTpRow
            ? `${escapeHtml(type)} (Lighthouse Resource Summary)`
            : escapeHtml(type);
          return `
          <div style="background: #0f172a; padding: 10px; border-radius: 6px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 14px;">${typeIcons[type] || '📦'}</span>
            <div>
              <div style="font-size: 12px; color: #e2e8f0; font-weight: 600;">${data.count || 0}</div>
              <div style="font-size: 10px; color: #64748b;">${typeLabel} (${formatBytes(data.size || 0)})</div>
            </div>
          </div>`;
        }).join('')}
      </div>` : ''}
      ${thirdPartyCount > 0 ? `
      <div style="background: linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(234, 88, 12, 0.1)); padding: 12px; border-radius: 8px; border: 1px solid rgba(249, 115, 22, 0.3);">
        <div style="font-size: 11px; color: #f97316; margin-bottom: 8px; font-weight: 600;">${lang === 'cs' ? 'Požadavky na jiné hostname (síťový záznam)' : 'Requests to other hostnames (network log)'}</div>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;">
          <div style="text-align: center;">
            <div style="font-size: 16px; font-weight: 700; color: #fb923c;">${thirdPartyCount}</div>
            <div style="font-size: 10px; color: #94a3b8;">${lang === 'cs' ? 'požadavků' : 'requests'}</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 16px; font-weight: 700; color: #fb923c;">${formatBytes(thirdPartySize)}</div>
            <div style="font-size: 10px; color: #94a3b8;">${lang === 'cs' ? 'dat' : 'data'}</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 16px; font-weight: 700; color: #fb923c;">${thirdPartyTime ? (thirdPartyTime >= 1000 ? (thirdPartyTime / 1000).toFixed(2).replace('.', lang === 'cs' ? ',' : '.') + ' s' : Math.round(thirdPartyTime) + 'ms') : '0ms'}</div>
            <div style="font-size: 10px; color: #94a3b8;">${lang === 'cs' ? 'čas' : 'time'}</div>
          </div>
        </div>
        <div style="font-size: 9px; color: #64748b; margin-top: 6px;">
          ${lang === 'cs'
            ? 'Měřeno ze síťového záznamu — jiná metodika než souhrn podle typu výše, počty nelze přímo porovnávat. „Cizí doména" = jiný hostname; může zahrnovat i vlastní CDN webu.'
            : 'Measured from the network log — a different methodology than the per-type summary above; counts are not directly comparable. "Third-party domain" = different hostname; it may include the site\'s own CDN.'}
        </div>
      </div>` : ''}
    </div>`;
  }

  if (includeW3C && (diagnostics?.mainThreadWork || diagnostics?.mainThreadBreakdown)) {
    const mtw = diagnostics.mainThreadWork || diagnostics.mainThreadBreakdown;
    const total = mtw.total || 1;
    const categoryLabels = {
      cs: {
        scriptEvaluation: 'Skripty', scriptParseCompile: 'Parse/Compile', styleLayout: 'Styly',
        paintCompositeRender: 'Render', garbageCollection: 'GC', parseHTML: 'HTML', other: 'Ostatní',
        Script: 'Skripty', 'Parse/Compile': 'Parse/Compile', Other: 'Ostatní', GC: 'GC', Style: 'Styly', Render: 'Render'
      },
      en: {
        scriptEvaluation: 'Scripts', scriptParseCompile: 'Parse/Compile', styleLayout: 'Styles',
        paintCompositeRender: 'Render', garbageCollection: 'GC', parseHTML: 'HTML', other: 'Other',
        Script: 'Scripts', 'Parse/Compile': 'Parse/Compile', Other: 'Other', GC: 'GC', Style: 'Styles', Render: 'Render'
      }
    };
    const categoryColors = {
      scriptEvaluation: '#fbbf24', scriptParseCompile: '#f97316', styleLayout: '#a855f7',
      paintCompositeRender: '#22c55e', garbageCollection: '#ef4444', parseHTML: '#3b82f6', other: '#64748b'
    };

    const hasCategories = mtw.categories && Array.isArray(mtw.categories) && mtw.categories.length > 0;
    const hasBreakdown = mtw.breakdown && Object.keys(mtw.breakdown).length > 0;

    html += `
    <div class="section">
      <div class="section-title">🧵 ${lang === 'cs' ? 'Práce hlavního vlákna' : 'Main Thread Work'} <span style="margin-left: auto; font-size: 16px; color: #f59e0b;">${(total / 1000).toFixed(2)}s</span></div>
      <p style="font-size: 11px; color: #94a3b8; margin-bottom: 12px;">
        ${lang === 'cs'
          ? 'Rozložení práce, kterou prohlížeč vykonává při načítání stránky. Vysoký podíl skriptů zpomaluje interaktivitu.'
          : 'Breakdown of work browser does when loading page. High script share slows interactivity.'}
      </p>
      ${hasCategories ? `
      <div style="height: 16px; border-radius: 8px; overflow: hidden; display: flex; background: #1e293b; margin-bottom: 12px;">
        ${mtw.categories.map(cat => {
          const color = categoryColors[cat.category] || '#64748b';
          return `<div style="width: ${cat.percentage}%; background: ${color};" title="${categoryLabels[lang]?.[cat.category] || cat.category}: ${cat.percentage}%"></div>`;
        }).join('')}
      </div>
      <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
        ${mtw.categories.slice(0, 6).map(cat => {
          const color = categoryColors[cat.category] || '#64748b';
          const label = categoryLabels[lang]?.[cat.category] || cat.category;
          return `
          <div style="display: flex; align-items: center; gap: 4px; font-size: 10px;">
            <span style="width: 8px; height: 8px; border-radius: 50%; background: ${color};"></span>
            <span style="color: #94a3b8;">${escapeHtml(label)}</span>
            <span style="color: white; font-weight: 600;">${cat.percentage}%</span>
          </div>`;
        }).join('')}
      </div>` : hasBreakdown ? `
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px;">
        ${Object.entries(mtw.breakdown).slice(0, 6).sort((a, b) => b[1] - a[1]).map(([category, ms]) => {
          const pct = Math.round((ms / total) * 100);
          const label = escapeHtml(categoryLabels[lang]?.[category] || category);
          return `
          <div style="background: #1e293b; padding: 10px; border-radius: 6px; text-align: center;">
            <div style="font-size: 14px; color: #e2e8f0; font-weight: 600;">${label}</div>
            <div style="font-size: 18px; color: #a855f7; font-weight: 700;">${pct}%</div>
          </div>`;
        }).join('')}
      </div>` : ''}
      ${mtw.topScripts?.length > 0 ? `
      <div>
        <div style="font-size: 11px; color: #94a3b8; margin-bottom: 8px;">${lang === 'cs' ? 'Nejnáročnější skripty:' : 'Most expensive scripts:'}</div>
        ${mtw.topScripts.slice(0, 3).map(script => {
          const name = script.url ? script.url.split('/').pop()?.slice(0, 35) || 'script' : 'Unattributable';
          const time = script.total || script.duration || script.time || 0;
          return `
          <div style="display: flex; justify-content: space-between; padding: 6px 10px; background: #0f172a; border-radius: 4px; margin-bottom: 4px;">
            <span style="font-size: 11px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(name)}</span>
            <span style="font-size: 11px; color: #f59e0b; font-weight: 600;">${Math.round(time)}ms</span>
          </div>`;
        }).join('')}
      </div>` : ''}
    </div>`;
  }

  if (includeAdvanced && diagnostics?.fontLoading) {
    const fonts = diagnostics.fontLoading;
    const formatBytes = (b) => {
      if (!b) return '0 B';
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
      return (b / (1024 * 1024)).toFixed(2) + ' MB';
    };
    const withoutSwapCount = fonts.fontsWithoutSwap?.length || 0;
    html += `
    <div class="section">
      <div class="section-title">🔤 ${lang === 'cs' ? 'Analýza fontů' : 'Font Loading Analysis'}</div>
      <p style="font-size: 11px; color: #94a3b8; margin-bottom: 12px;">
        ${lang === 'cs'
          ? 'Analýza načítání fontů. FOIT = neviditelný text, FOUT = změna stylu textu po načtení fontu.'
          : 'Font loading analysis. FOIT = invisible text, FOUT = text style change after font loads.'}
      </p>
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;">
        <div style="background: #1e293b; padding: 12px; border-radius: 8px; text-align: center;">
          <div style="font-size: 18px; font-weight: 700; color: #3b82f6;">${fonts.totalFonts || 0}</div>
          <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Fontů' : 'Fonts'}</div>
        </div>
        <div style="background: #1e293b; padding: 12px; border-radius: 8px; text-align: center;">
          <div style="font-size: 18px; font-weight: 700; color: ${withoutSwapCount > 0 ? '#ef4444' : '#22c55e'};">${withoutSwapCount}</div>
          <div style="font-size: 10px; color: #64748b;">Bez swap</div>
        </div>
        <div style="background: #1e293b; padding: 12px; border-radius: 8px; text-align: center;">
          <div style="font-size: 18px; font-weight: 700; color: ${fonts.foitRisk ? '#ef4444' : '#22c55e'};">${fonts.foitRisk ? '✗' : '✓'}</div>
          <div style="font-size: 10px; color: #64748b;">FOIT</div>
        </div>
        <div style="background: #1e293b; padding: 12px; border-radius: 8px; text-align: center;">
          <div style="font-size: 18px; font-weight: 700; color: ${fonts.foutRisk ? '#f59e0b' : '#22c55e'};">${fonts.foutRisk ? '✗' : '✓'}</div>
          <div style="font-size: 10px; color: #64748b;">FOUT</div>
        </div>
      </div>
      ${fonts.fontDetails?.length > 0 ? `
      <div style="font-size: 11px; color: #94a3b8; margin-bottom: 8px;">${lang === 'cs' ? 'Detaily fontů:' : 'Font details:'}</div>
      <div style="space-y: 4px;">
        ${fonts.fontDetails.slice(0, 5).map(font => {
          const name = font.url ? font.url.split('/').pop()?.slice(0, 45) || 'font' : font.name || 'font';
          return `
          <div style="display: flex; justify-content: space-between; padding: 6px 10px; background: #0f172a; border-radius: 4px; margin-bottom: 4px;">
            <span style="font-size: 11px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; max-width: 60%;">${escapeHtml(name)}</span>
            <div style="display: flex; gap: 12px;">
              <span style="font-size: 11px; color: #64748b;">${formatBytes(font.size || font.transferSize || 0)}</span>
              <span style="font-size: 11px; color: #3b82f6;">${font.duration ? Math.round(font.duration * 1000) + ' ms' : '0 ms'}</span>
            </div>
          </div>`;
        }).join('')}
      </div>
      ` : ''}
    </div>`;
  }

  // === SECTION: Render Blocking Resources ===
  if (includeAdvanced && (diagnostics?.renderBlocking || diagnostics?.criticalRenderingPath)) {
    const rb = diagnostics.renderBlocking || {};
    const crp = diagnostics.criticalRenderingPath || {};
    const resources = rb.resources || crp.renderBlockingResources || [];
    const totalBlockingTime = rb.totalBlockingTime || crp.totalBlockingTime || 0;
    const totalBlockingBytes = rb.totalBlockingBytes || crp.totalBlockingBytes || 0;
    const chainDepth = crp.criticalChainDepth || 0;

    const formatBytes = (b) => {
      if (!b) return '0 B';
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
      return (b / (1024 * 1024)).toFixed(2) + ' MB';
    };

    if (resources.length > 0 || totalBlockingTime > 0) {
      html += `
    <div class="section">
      <div class="section-title">🚧 ${lang === 'cs' ? 'Render-Blocking zdroje' : 'Render-Blocking Resources'}</div>
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px;">
        <div style="background: ${resources.length > 0 ? 'rgba(239, 68, 68, 0.2)' : '#1e293b'}; padding: 12px; border-radius: 8px; text-align: center;">
          <div style="font-size: 20px; font-weight: 700; color: ${resources.length > 0 ? '#ef4444' : '#22c55e'};">${resources.length}</div>
          <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Zdrojů' : 'Resources'}</div>
        </div>
        <div style="background: #1e293b; padding: 12px; border-radius: 8px; text-align: center;">
          <div style="font-size: 20px; font-weight: 700; color: #f59e0b;">${Math.round(totalBlockingTime)}ms</div>
          <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Blokující čas' : 'Blocking time'}</div>
        </div>
        <div style="background: #1e293b; padding: 12px; border-radius: 8px; text-align: center;">
          <div style="font-size: 20px; font-weight: 700; color: #3b82f6;">${formatBytes(totalBlockingBytes)}</div>
          <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Velikost' : 'Size'}</div>
        </div>
      </div>
      ${chainDepth > 0 ? `
      <div style="background: #0f172a; padding: 12px; border-radius: 8px; margin-bottom: 12px;">
        <div style="font-size: 12px; color: #94a3b8;">${lang === 'cs' ? 'Hloubka kritické cesty' : 'Critical chain depth'}: <span style="color: #f59e0b; font-weight: 600;">${chainDepth}</span></div>
      </div>
      ` : ''}
      ${resources.length > 0 ? `
      <div style="font-size: 11px; color: #94a3b8; margin-bottom: 8px;">${lang === 'cs' ? 'Blokující zdroje:' : 'Blocking resources:'}</div>
      ${resources.slice(0, 6).map(r => {
        const rawFilename = r.url ? r.url.split('/').pop()?.slice(0, 40) || r.url.slice(0, 40) : 'unknown';
        const filename = escapeHtml(rawFilename);
        const typeIcon = r.type === 'css' ? '🎨' : r.type === 'js' ? '📜' : r.type === 'font' || r.type === 'woff2' ? '🔤' : '📦';
        return `
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #1e293b; border-radius: 6px; margin-bottom: 6px;">
        <div style="display: flex; align-items: center; gap: 8px; max-width: 60%; overflow: hidden;">
          <span style="font-size: 12px;">${typeIcon}</span>
          <span style="font-size: 11px; color: #e2e8f0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${filename}</span>
        </div>
        <div style="display: flex; gap: 12px;">
          <span style="font-size: 11px; color: #64748b;">${formatBytes(r.totalBytes || 0)}</span>
          <span style="font-size: 11px; color: #ef4444;">-${Math.round(r.wastedMs || 0)}ms</span>
        </div>
      </div>`;
      }).join('')}
      ` : ''}
    </div>`;
    }
  }

  if (includeAdvanced && diagnostics?.imageOptimization) {
    const img = diagnostics.imageOptimization;
    const formatBytes = (b) => {
      if (!b) return '0 B';
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
      return (b / (1024 * 1024)).toFixed(2) + ' MB';
    };
    const getScoreColor = (score) => {
      if (score >= 0.9) return '#22c55e';
      if (score >= 0.5) return '#f59e0b';
      return '#ef4444';
    };
    html += `
    <div class="section">
      <div class="section-title">🖼️ ${lang === 'cs' ? 'Optimalizace obrázků' : 'Image Optimization'}</div>
      <p style="font-size: 11px; color: #94a3b8; margin-bottom: 12px;">
        ${lang === 'cs'
          ? 'Obrázky jsou často největší částí stránky. Optimalizace zrychlí načítání a sníží datový objem.'
          : 'Images are often the largest part of a page. Optimization speeds up loading and reduces data.'}
      </p>
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 16px;">
        <div style="background: ${img.totalIssues > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)'}; padding: 16px; border-radius: 8px; text-align: center;">
          <div style="font-size: 24px; font-weight: 700; color: ${img.totalIssues > 0 ? '#ef4444' : '#22c55e'};">${img.totalIssues || 0}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'Problémů' : 'Issues'}</div>
        </div>
        <div style="background: #1e293b; padding: 16px; border-radius: 8px; text-align: center;">
          <div style="font-size: 24px; font-weight: 700; color: #f59e0b;">${formatBytes(img.totalWastedBytes || 0)}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'Zbytečná data' : 'Wasted'}</div>
        </div>
      </div>
      ${img.scores && Object.keys(img.scores).length > 0 ? `
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px;">
        ${Object.entries(img.scores).map(([key, score]) => `
          <div style="background: #0f172a; padding: 8px; border-radius: 6px; text-align: center;">
            <div style="font-size: 14px; font-weight: 700; color: ${getScoreColor(score)};">${score !== null ? Math.round((score || 0) * 100) : '?'}</div>
            <div style="font-size: 9px; color: #64748b; text-transform: capitalize;">${key}</div>
          </div>
        `).join('')}
      </div>
      ` : ''}
      ${img.needsModernFormat?.length > 0 ? `
      <div style="margin-bottom: 12px; padding: 10px; background: #0f172a; border-radius: 6px;">
        <div style="font-size: 11px; color: #f59e0b; margin-bottom: 6px;">🖼️ ${lang === 'cs' ? 'Potřebuje WebP/AVIF' : 'Needs WebP/AVIF'} (${img.needsModernFormat.length})</div>
        <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Možná úspora:' : 'Potential savings:'} ${formatBytes(img.needsModernFormat.reduce((s, i) => s + (i.wastedBytes || 0), 0))}</div>
      </div>
      ` : ''}
      ${img.needsLazyLoading?.length > 0 ? `
      <div style="margin-bottom: 12px; padding: 10px; background: #0f172a; border-radius: 6px;">
        <div style="font-size: 11px; color: #3b82f6; margin-bottom: 6px;">⏳ ${lang === 'cs' ? 'Potřebuje lazy loading' : 'Needs lazy loading'} (${img.needsLazyLoading.length})</div>
        <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Odložitelná data:' : 'Deferrable:'} ${formatBytes(img.needsLazyLoading.reduce((s, i) => s + (i.wastedBytes || 0), 0))}</div>
      </div>
      ` : ''}
      ${img.unsizedImages?.length > 0 ? `
      <div style="padding: 10px; background: #0f172a; border-radius: 6px;">
        <div style="font-size: 11px; color: #ef4444; margin-bottom: 6px;">📐 ${lang === 'cs' ? 'Bez rozměrů (CLS riziko)' : 'Missing dimensions (CLS risk)'} (${img.unsizedImages.length})</div>
      </div>
      ` : ''}
    </div>`;
  }

  if (includeAdvanced && diagnostics?.performanceBudget) {
    const pb = diagnostics.performanceBudget;
    const formatBytes = (b) => {
      if (!b) return '0 B';
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
      return (b / (1024 * 1024)).toFixed(2) + ' MB';
    };
    const typeLabels = {
      total: lang === 'cs' ? 'Celkem' : 'Total',
      script: 'JavaScript',
      stylesheet: 'CSS',
      image: lang === 'cs' ? 'Obrázky' : 'Images',
      font: lang === 'cs' ? 'Fonty' : 'Fonts',
      document: 'HTML',
      other: lang === 'cs' ? 'Ostatní' : 'Other'
    };
    const getBudgetColor = (percentage) => {
      if (percentage <= 70) return '#22c55e';
      if (percentage <= 100) return '#f59e0b';
      return '#ef4444';
    };
    const overBudgetItems = pb.budgetStatus?.filter(b => b.overBudget) || [];

    html += `
    <div class="section">
      <div class="section-title">📊 Performance Budget</div>
      <p style="font-size: 11px; color: #94a3b8; margin-bottom: 12px;">
        ${lang === 'cs'
          ? 'Performance budget je interní doporučený benchmark ControlVitals (ne oficiální limit Google Core Web Vitals) pro maximální velikost zdrojů pro rychlé načtení. Překročení doporučených limitů zpomaluje web.'
          : 'The performance budget is ControlVitals\' internal recommended benchmark (not an official Google Core Web Vitals limit) for maximum resource sizes for fast loading. Exceeding the recommended limits slows down your site.'}
      </p>
      ${pb.budgetStatus?.length > 0 ? `
      <div style="margin-bottom: 16px;">
        ${pb.budgetStatus.map(b => {
          const actual = b.current || b.actual || 0;
          const budget = b.limit || b.budget || 0;
          const pct = b.percentage || (budget > 0 ? Math.min(150, (actual / budget) * 100) : 0);
          const color = getBudgetColor(pct);
          return `
          <div style="margin-bottom: 10px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span style="font-size: 12px; color: #94a3b8;">${typeLabels[b.type] || b.type}</span>
              <span style="font-size: 12px; color: ${b.overBudget ? '#ef4444' : '#22c55e'};">${formatBytes(actual)} / ${formatBytes(budget)} ${b.overBudget ? '⚠️' : '✓'}</span>
            </div>
            <div style="height: 6px; background: #334155; border-radius: 3px; overflow: hidden;">
              <div style="height: 100%; width: ${Math.min(100, pct)}%; background: ${color}; border-radius: 3px;"></div>
            </div>
          </div>`;
        }).join('')}
      </div>
      ` : ''}
      ${(pb.unusedJs?.wastedBytes > 0 || pb.unusedCss?.wastedBytes > 0) ? `
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 12px;">
        ${pb.unusedJs?.wastedBytes > 0 ? `
        <div style="background: #0f172a; padding: 12px; border-radius: 6px; text-align: center;">
          <div style="font-size: 16px; font-weight: 700; color: #f59e0b;">${formatBytes(pb.unusedJs.wastedBytes)}</div>
          <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Nepoužitý JS' : 'Unused JS'}</div>
        </div>
        ` : ''}
        ${pb.unusedCss?.wastedBytes > 0 ? `
        <div style="background: #0f172a; padding: 12px; border-radius: 6px; text-align: center;">
          <div style="font-size: 16px; font-weight: 700; color: #ec4899;">${formatBytes(pb.unusedCss.wastedBytes)}</div>
          <div style="font-size: 10px; color: #64748b;">${lang === 'cs' ? 'Nepoužité CSS' : 'Unused CSS'}</div>
        </div>
        ` : ''}
      </div>
      ` : ''}
      ${overBudgetItems.length > 0 ? `
      <div style="padding: 10px; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px;">
        <div style="font-size: 11px; font-weight: 600; color: #ef4444; margin-bottom: 6px;">
          ⚠️ ${lang === 'cs' ? 'Překročené limity' : 'Over Budget'}
        </div>
        <div style="font-size: 10px; color: #94a3b8;">
          ${overBudgetItems.map(b => typeLabels[b.type] || b.type).join(', ')}
        </div>
      </div>
      ` : ''}
    </div>`;
  }

  if (includeAdvanced && diagnostics?.unusedResources) {
    const unused = diagnostics.unusedResources;
    const formatBytes = (b) => {
      if (!b) return '0 B';
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
      return (b / (1024 * 1024)).toFixed(2) + ' MB';
    };

    if (unused.javascript?.totalWastedBytes > 0 || unused.css?.totalWastedBytes > 0) {
      html += `
      <div class="section">
        <div class="section-title">🗑️ ${lang === 'cs' ? 'Nepoužitý kód' : 'Unused Code'}</div>
        <div style="font-size: 10px; color: #64748b; margin-bottom: 10px;">
          ${lang === 'cs'
            ? 'Dle Lighthouse — studený load bez interakcí. Jde o horní odhad: část tohoto kódu se spustí až po kliknutí/scrollu. Pro rozhodování, kolik kódu lze reálně odstranit, je směrodatné runtime měření „Pokrytí kódu" (po interakcích) — to ukazuje kód, který se nespustí nikdy.'
            : 'Per Lighthouse — cold load, no interactions. This is an upper bound: some of this code runs only after click/scroll. For deciding how much code can actually be removed, the runtime "Code Coverage" measurement (after interactions) is authoritative — it shows code that never runs.'}
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 16px;">
          <div style="background: #1e293b; padding: 16px; border-radius: 8px; text-align: center;">
            <div style="font-size: 20px; font-weight: 700; color: #ec4899;">${formatBytes(unused.css?.totalWastedBytes || 0)}</div>
            <div style="font-size: 11px; color: #64748b;">${lang === 'cs' ? 'Nepoužité CSS' : 'Unused CSS'}</div>
          </div>
          <div style="background: #1e293b; padding: 16px; border-radius: 8px; text-align: center;">
            <div style="font-size: 20px; font-weight: 700; color: #f59e0b;">${formatBytes(unused.javascript?.totalWastedBytes || 0)}</div>
            <div style="font-size: 11px; color: #64748b;">${lang === 'cs' ? 'Nepoužitý JS' : 'Unused JS'}</div>
          </div>
        </div>
        ${unused.javascript?.items?.length > 0 ? `
        <div style="margin-bottom: 12px;">
          <div style="font-size: 11px; color: #f59e0b; margin-bottom: 6px; display: flex; align-items: center; gap: 4px;">
            ⚡ JavaScript (${unused.javascript.items.length} ${lang === 'cs' ? 'souborů' : 'files'})
          </div>
          ${unused.javascript.items.slice(0, 3).map(item => `
            <div style="display: flex; justify-content: space-between; padding: 6px 10px; background: #0f172a; border-radius: 4px; margin-bottom: 4px;">
              <span style="font-size: 10px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; max-width: 70%;">• ${escapeHtml((item.url || '').replace(/^https?:\/\//, '').slice(0, 50))}...</span>
              <span style="font-size: 10px; color: #f59e0b;">${item.wastedPercent?.toFixed(0) || 0}%</span>
            </div>
          `).join('')}
        </div>
        ` : ''}
        ${unused.css?.items?.length > 0 ? `
        <div>
          <div style="font-size: 11px; color: #ec4899; margin-bottom: 6px; display: flex; align-items: center; gap: 4px;">
            🎨 CSS (${unused.css.items.length} ${lang === 'cs' ? 'souborů' : 'files'})
          </div>
          ${unused.css.items.slice(0, 3).map(item => `
            <div style="display: flex; justify-content: space-between; padding: 6px 10px; background: #0f172a; border-radius: 4px; margin-bottom: 4px;">
              <span style="font-size: 10px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; max-width: 70%;">• ${escapeHtml((item.url || '').replace(/^https?:\/\//, '').slice(0, 50))}...</span>
              <span style="font-size: 10px; color: #ec4899;">${item.wastedPercent?.toFixed(0) || 0}%</span>
            </div>
          `).join('')}
        </div>
        ` : ''}
      </div>`;
    }
  }

  if (validDeepInp) {
    const inpValue = comparableLabSummary(deepINPResults.labRunSpread)?.median ?? deepINPResults.inp;
    const inpColor = inpValue <= 200 ? '#22c55e' : inpValue <= 500 ? '#f59e0b' : '#ef4444';
    const inpLabel = inpValue <= 200 ? (lang === 'cs' ? 'Dobré' : 'Good') : inpValue <= 500 ? (lang === 'cs' ? 'K zlepšení' : 'Needs Improvement') : (lang === 'cs' ? 'Špatné' : 'Poor');

    html += `
    <div class="section" style="border: 1px solid ${inpColor}30;">
      <div class="section-title">🔬 ${lang === 'cs' ? 'Hloubková INP analýza' : 'Deep INP Analysis'}</div>

      <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 20px;">
        <div style="padding: 12px 24px; background: ${inpColor}15; border: 2px solid ${inpColor}; border-radius: 12px; text-align: center;">
          <div style="font-size: 28px; font-weight: 700; color: ${inpColor};">${Math.round(inpValue)}ms</div>
          <div style="font-size: 11px; color: #94a3b8;">INP</div>
        </div>
        <div style="padding: 4px 12px; background: ${inpColor}20; color: ${inpColor}; border-radius: 16px; font-size: 12px; font-weight: 600;">${inpLabel}</div>
        <div style="padding: 4px 12px; background: #33415560; color: #cbd5e1; border-radius: 16px; font-size: 11px;">${deepINPResults.device === 'desktop' ? (lang === 'cs' ? '🖥️ Desktop (emulace)' : '🖥️ Desktop (emulated)') : deepINPResults.device === 'tablet' ? (lang === 'cs' ? 'Tablet (emulace)' : 'Tablet (emulated)') : (lang === 'cs' ? '📱 Mobil (emulace)' : '📱 Mobile (emulated)')}</div>
        <div style="padding: 4px 12px; background: #6366f120; color: #818cf8; border-radius: 16px; font-size: 11px;">${formatConsentState(deepINPResults, lang)}</div>
      </div>
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 20px;">
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 16px; text-align: center;">
          <div style="font-size: 11px; color: #94a3b8; margin-bottom: 4px;">DOM ${lang === 'cs' ? 'elementy' : 'Elements'}</div>
          <div style="font-size: 20px; font-weight: 700; color: ${deepINPResults.domSizeSuspect ? '#94a3b8' : (deepINPResults.domSize || 0) > 1500 ? '#f59e0b' : '#22c55e'};">${deepINPResults.domSize || 0}</div>
          ${deepINPResults.domSizeSuspect ? `<div style="font-size: 10px; color: #f59e0b; margin-top: 2px;">${lang === 'cs' ? 'měření nespolehlivé' : 'measurement unreliable'}</div>` : ''}
        </div>
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 16px; text-align: center;">
          <div style="font-size: 11px; color: #94a3b8; margin-bottom: 4px;">${lang === 'cs' ? 'Interakce' : 'Interactions'}</div>
          <div style="font-size: 20px; font-weight: 700; color: #60a5fa;">${deepINPResults.interactions?.length || 0}</div>
        </div>
      </div>`;

    html += renderInteractionDiagnosticsHtml(deepINPResults.interactionDiagnostics, lang, 'dark');

    if (deepINPResults.worstInteraction) {
      const wi = deepINPResults.worstInteraction;
      const inputDelay = Math.round(wi.inputDelay || 0);
      const processingTime = Math.round(wi.processingTime || 0);
      const presentationDelay = Math.round(wi.presentationDelay || 0);
      const total = inputDelay + processingTime + presentationDelay;
      const idPct = total > 0 ? Math.round((inputDelay / total) * 100) : 33;
      const ptPct = total > 0 ? Math.round((processingTime / total) * 100) : 34;
      const pdPct = total > 0 ? 100 - idPct - ptPct : 33;

      html += `
      <div style="margin-bottom: 20px;">
        <div style="font-size: 12px; font-weight: 600; color: #e2e8f0; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.05em;">${lang === 'cs' ? 'Nejhorší interakce' : 'Worst Interaction'}</div>
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 12px; padding: 16px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
            <span style="font-size: 13px; font-weight: 600; color: #c084fc;">👆 ${escapeHtml(wi.name || 'click')}</span>
            ${wi.target ? `<span style="font-size: 11px; color: #94a3b8; background: #0f172a; padding: 2px 8px; border-radius: 4px;">${escapeHtml(wi.target.tagName?.toLowerCase() || '')}</span>` : ''}
            ${wi.loadState ? `<span style="font-size: 10px; padding: 2px 8px; border-radius: 10px; background: ${wi.loadState === 'complete' ? '#22c55e20' : '#f59e0b20'}; color: ${wi.loadState === 'complete' ? '#4ade80' : '#fbbf24'};">${wi.loadState === 'complete' ? (lang === 'cs' ? 'po načtení' : 'after load') : (lang === 'cs' ? 'během načítání' : 'during load')}</span>` : ''}
          </div>
          <div style="display: flex; height: 24px; border-radius: 6px; overflow: hidden; margin-bottom: 12px;">
            <div style="width: ${idPct}%; background: #f59e0b; display: flex; align-items: center; justify-content: center;">
              ${idPct > 10 ? `<span style="font-size: 9px; color: #000; font-weight: 600;">${inputDelay}ms</span>` : ''}
            </div>
            <div style="width: ${ptPct}%; background: #3b82f6; display: flex; align-items: center; justify-content: center;">
              ${ptPct > 10 ? `<span style="font-size: 9px; color: white; font-weight: 600;">${processingTime}ms</span>` : ''}
            </div>
            <div style="width: ${pdPct}%; background: #22c55e; display: flex; align-items: center; justify-content: center;">
              ${pdPct > 10 ? `<span style="font-size: 9px; color: #000; font-weight: 600;">${presentationDelay}ms</span>` : ''}
            </div>
          </div>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;">
            <div style="text-align: center;">
              <div style="font-size: 10px; color: #f59e0b;">● ${lang === 'cs' ? 'Zpoždění vstupu' : 'Input Delay'}</div>
              <div style="font-size: 13px; font-weight: 600; color: #e2e8f0;">${inputDelay}ms</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 10px; color: #3b82f6;">● ${lang === 'cs' ? 'Zpracování' : 'Processing'}</div>
              <div style="font-size: 13px; font-weight: 600; color: #e2e8f0;">${processingTime}ms</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 10px; color: #22c55e;">● ${lang === 'cs' ? 'Vykreslení' : 'Presentation'}</div>
              <div style="font-size: 13px; font-weight: 600; color: #e2e8f0;">${presentationDelay}ms</div>
            </div>
          </div>
        </div>
      </div>`;
    }

    const inpAttr = deepINPResults.attribution;
    const inpAttrInvalid = inpAttr && (inpAttr.attributionFailed || deepINPResults.attributionValid === false);
    if (inpAttrInvalid) {
      const reason = deepINPResults.navigatedTo
        ? (lang === 'cs' ? `měřená interakce navigovala na jinou stránku (${escapeHtml(deepINPResults.navigatedTo)})` : `the measured interaction navigated to another page (${escapeHtml(deepINPResults.navigatedTo)})`)
        : deepINPResults.attributionInvalidReason === 'dom-sanity'
        ? (lang === 'cs' ? 'měření zachytilo jiný dokument (podezřele malý DOM)' : 'the run captured a different document (suspiciously small DOM)')
        : (lang === 'cs' ? `příliš velká část blokujícího času (${Math.round((inpAttr.undeterminedShare || 0) * 100)} %) nejde přiřadit konkrétnímu skriptu` : `too much blocking time (${Math.round((inpAttr.undeterminedShare || 0) * 100)}%) cannot be attributed to a specific script`);
      html += `
      <div style="margin-bottom: 20px;">
        <div style="font-size: 12px; font-weight: 600; color: #22d3ee; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;">${lang === 'cs' ? 'Atribuce blokujícího času' : 'Blocking Time Attribution'}</div>
        <div style="background: #1e293b; border: 1px solid #f59e0b40; border-radius: 12px; padding: 16px; font-size: 12px; color: #fbbf24;">
          ${lang === 'cs' ? 'Atribuce selhala — ' : 'Attribution failed — '}${reason}. ${lang === 'cs' ? 'Rozdělení na první/třetí strany proto nezobrazujeme, bylo by zavádějící.' : 'The first-/third-party split is therefore not shown as it would be misleading.'}
        </div>
      </div>`;
    } else if (inpAttr?.scriptBlockingMs > 0) {
      const tpPct = Math.min(100, Math.round((inpAttr.thirdPartyShare || 0) * 100));
      const tpColor = tpPct > 60 ? '#ef4444' : tpPct > 30 ? '#f59e0b' : '#22c55e';
      html += `
      <div style="margin-bottom: 20px;">
        <div style="font-size: 12px; font-weight: 600; color: #22d3ee; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;">${lang === 'cs' ? 'Atribuce blokujícího času' : 'Blocking Time Attribution'}</div>
        <div style="font-size: 10px; color: #64748b; margin-bottom: 8px;">${inpAttr.windowBounded === false
          ? (lang === 'cs'
            ? 'Nepodařilo se zachytit interakci — čísla níže pokrývají celý běh včetně načtení stránky, ne jen interakční okno.'
            : 'No interaction was captured — the numbers below cover the whole run including page load, not just the interaction window.')
          : (lang === 'cs'
              ? `Diagnostická okna nejsou jednoznačně propojena s reprezentativní interakcí ani se zdrojovým pokusem rozpadu fází. Naměřených ${Math.round(inpAttr.scriptBlockingMs)} ms práce skriptů proto nelze použít k určení podílu třetích stran ani viníka této interakce.`
              : `The diagnostic windows are not explicitly linked to the representative interaction or the source attempt of the phase breakdown. The measured ${Math.round(inpAttr.scriptBlockingMs)} ms of script work therefore cannot determine the third-party share or culprit of that interaction.`)}</div>
        ${inpAttr.windowBounded === false ? `<div style="background: #1e293b; border: 1px solid #334155; border-radius: 12px; padding: 16px;">
          <div style="display: flex; gap: 24px; margin-bottom: 10px; font-size: 12px; color: #94a3b8;">
            <span>${lang === 'cs' ? 'Skripty celkem:' : 'Total script time:'} <strong style="color: #e2e8f0;">${Math.round(inpAttr.scriptBlockingMs)}ms</strong></span>
            <span>${lang === 'cs' ? 'Třetí strany:' : 'Third-party:'} <strong style="color: ${tpColor};">${Math.round(inpAttr.thirdPartyBlockingMs)}ms (${tpPct}%)</strong></span>
          </div>
          <div style="height: 8px; background: #334155; border-radius: 4px; overflow: hidden; margin-bottom: 10px;">
            <div style="width: ${Math.min(tpPct, 100)}%; height: 100%; background: ${tpColor};"></div>
          </div>
          ${(() => {
            // Single source of truth: name the SAME task that leads the
            // long-tasks table below — never a second field from a different
            // measurement API (would show two conflicting "worst" scripts).
            const topLt = (deepINPResults.longTasks || []).slice().sort((a, b) => (b.duration || 0) - (a.duration || 0))[0];
            if (!topLt) return '';
            const nm = topLt.scripts?.[0]?.sourceURL || topLt.name || 'unknown';
            return `<div style="font-size: 11px; color: #94a3b8; word-break: break-all;">${lang === 'cs' ? 'Nejdéle blokující úloha:' : 'Longest blocking task:'} <span style="color: #67e8f9;">${escapeHtml(nm)}</span> <span style="color: #fbbf24;">(${Math.round(topLt.duration || 0)}ms)</span> <span style="color: #64748b;">${lang === 'cs' ? '— stejný záznam vede i tabulku dlouhých úloh níže' : '— the same entry leads the long-tasks table below'}</span></div>`;
          })()}
          ${(inpAttr.outOfScopeMs > 0 || inpAttr.undeterminedMs > 0) ? `<div style="font-size: 10px; color: #64748b; margin-top: 6px;">${lang === 'cs'
            ? `Mimo měřenou stránku (iframe apod.): ${Math.round(inpAttr.outOfScopeMs || 0)}ms · nepřiřazeno: ${Math.round(inpAttr.undeterminedMs || 0)}ms — do procent výše se nepočítá.`
            : `Outside the measured page (iframes etc.): ${Math.round(inpAttr.outOfScopeMs || 0)}ms · unattributed: ${Math.round(inpAttr.undeterminedMs || 0)}ms — excluded from the percentages above.`}</div>` : ''}
        </div>` : ''}
      </div>`;
    }

    if (validLongTasks(deepINPResults.longTasks).length > 0) {
      const tasks = validLongTasks(deepINPResults.longTasks);
      const tbt = tasks.reduce((sum, lt) => sum + Math.max(0, lt.duration - 50), 0);
      html += `
      <div style="margin-bottom: 16px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 12px; font-weight: 600; color: #e2e8f0; text-transform: uppercase; letter-spacing: 0.05em;">${lang === 'cs' ? 'Dlouhé úlohy — celý běh' : 'Long Tasks — whole run'} (${tasks.length}${Array.isArray(deepINPResults.longTasksInWindow) && !longTaskCountDiscrepancy(deepINPResults, lang) ? (lang === 'cs' ? `, z toho ${validLongTasks(deepINPResults.longTasksInWindow).length} v interakčním okně` : `, ${validLongTasks(deepINPResults.longTasksInWindow).length} within the interaction window`) : ''})</span>
          <span style="font-size: 12px; color: #94a3b8;">${lang === 'cs' ? 'Blokující čas dlouhých úloh (celý běh)' : 'Long-task blocking time (whole run)'}: <span style="color: ${tbt > 300 ? '#ef4444' : tbt > 150 ? '#f59e0b' : '#22c55e'}; font-weight: 600;">${Math.round(tbt)}ms</span></span>
        </div>
        ${longTaskCountDiscrepancy(deepINPResults, lang) ? `<p style="font-size:12px;color:#fbbf24;">${escapeHtml(longTaskCountDiscrepancy(deepINPResults, lang))}</p>` : ''}`;

      const maxDuration = Math.max(1, ...tasks.map(t => t.duration || 0));
      tasks.slice().sort((a, b) => (b.duration || 0) - (a.duration || 0)).slice(0, 10).forEach(lt => {
        const blockTime = Math.max(0, (lt.duration || 0) - 50);
        const barWidth = Math.min(100, ((lt.duration || 0) / maxDuration) * 100);
        const barColor = lt.duration > 200 ? '#ef4444' : lt.duration > 100 ? '#f59e0b' : '#22c55e';
        const scriptInfo = lt.scripts?.[0];
        html += `
        <div style="background: #1e293b; border-radius: 6px; padding: 8px 12px; margin-bottom: 4px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="flex: 1; margin-right: 12px;">
              <div style="height: 8px; background: #334155; border-radius: 4px; overflow: hidden;">
                <div style="width: ${barWidth}%; height: 100%; background: ${barColor}; border-radius: 4px;"></div>
              </div>
              ${scriptInfo?.sourceURL ? `<div style="font-size: 10px; color: #64748b; margin-top: 4px; word-break: break-all;">${escapeHtml(scriptInfo.sourceURL.replace(/^https?:\/\//, '').slice(0, 60))}</div>` : `<div style="font-size: 10px; color: #64748b; margin-top: 4px;">${escapeHtml(lt.name || 'unknown')}</div>`}
            </div>
            <div style="text-align: right; white-space: nowrap;">
              <span style="font-size: 13px; font-weight: 600; color: ${barColor};">${Math.round(lt.duration)}ms</span>
              <span style="font-size: 10px; color: #94a3b8; margin-left: 4px;">+${Math.round(blockTime)}ms block</span>
            </div>
          </div>
        </div>`;
      });

      html += `</div>`;
    }

    const inpCauses = Array.isArray(deepINPResults.causes)
      ? deepINPResults.causes.filter(Boolean).map(normalizeInpDiagnosticCause)
      : [];
    if (inpCauses.length > 0) {
      const inpCauseLabels = inpCauseContent.labels;
      const inpCauseExamples = inpCauseContent.examples;
      const inpSevColor = severityColor;
      const inpSevLabel = (s) => severityLabels[s]?.[lang] || severityLabels.low[lang];

      html += `
    <div style="margin-top: 20px;">
      <div style="font-size: 12px; font-weight: 600; color: #fb923c; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px;">🎯 ${lang === 'cs' ? 'Pozorovaní kandidáti a postup ověření' : 'Observed candidates & verification steps'}</div>`;

      inpCauses.forEach(cause => {
        const key = cause.cause;
        const label = inpCauseLabels[key]?.[lang] || key || (lang === 'cs' ? 'Neznámá příčina' : 'Unknown cause');
        const desc = inpCauseDescription(cause, lang);
        const recs = inpCauseRecommendationsFor(cause, lang);
        const ex = inpCauseExamples[key];
        const sev = inpDiagnosticSeverity(cause);
        const color = inpSevColor(sev);
        html += `
      <div class="task-card" style="background: ${color}1a; border: 1px solid ${color}4d;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span class="task-priority" style="background: ${color}; color: white;">${escapeHtml(inpSevLabel(sev))}</span>
          <span style="font-size: 14px; font-weight: 600; color: white;">${escapeHtml(label)}</span>
        </div>
        ${desc ? `<div style="font-size: 12px; color: #cbd5e1; margin-bottom: 10px;">${escapeHtml(desc)}</div>` : ''}
        ${key === 'long-tasks' && longTaskCountDiscrepancy(deepINPResults, lang)
          ? `<div style="font-size:11px;color:#fbbf24;margin-bottom:10px;">${escapeHtml(longTaskCountDiscrepancy(deepINPResults, lang))}</div>`
          : key === 'long-tasks' && longTaskScopeDetail(cause, lang)
          ? `<div style="font-size: 11px; color: #94a3b8; font-style: italic; margin-bottom: 10px;">${escapeHtml(longTaskScopeDetail(cause, lang))}</div>`
          : ''}
        ${recs.length > 0 ? `
        <div style="margin-bottom: 10px;">
          <div style="font-size: 11px; font-weight: 600; color: #cbd5e1; margin-bottom: 6px;">${lang === 'cs' ? 'Doporučení:' : 'Fix:'}</div>
          <ul style="margin-left: 18px; color: #94a3b8; font-size: 12px; line-height: 1.6;">
            ${recs.map(r => `<li>${escapeHtml(r)}</li>`).join('')}
          </ul>
        </div>` : ''}
        ${ex ? `
        <div style="margin-top: 10px;">
          <div style="font-size: 11px; font-weight: 600; color: #cbd5e1; margin-bottom: 6px;">${lang === 'cs' ? 'Jak opravit:' : 'How to fix:'}</div>
          <div style="font-size: 10px; color: #94a3b8; line-height: 1.5; margin-bottom: 6px;">${escapeHtml(inpGenericCodeExampleLabel[lang])}</div>
          <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px; padding: 10px; margin-bottom: 6px;">
            <div style="font-size: 10px; color: #f87171; margin-bottom: 4px;">✗ ${lang === 'cs' ? 'Obecný rizikový vzor' : 'Generic risk pattern'}</div>
            <pre style="font-size: 11px; color: #fca5a5; white-space: pre-wrap; font-family: 'Monaco', 'Consolas', monospace; margin: 0;">${escapeHtml(ex.bad)}</pre>
          </div>
          <div style="background: rgba(34, 197, 94, 0.1); border: 1px solid rgba(34, 197, 94, 0.3); border-radius: 6px; padding: 10px;">
            <div style="font-size: 10px; color: #4ade80; margin-bottom: 4px;">✓ ${lang === 'cs' ? 'Možná varianta po potvrzení profilem' : 'Possible variant after profile confirmation'}</div>
            <pre style="font-size: 11px; color: #86efac; white-space: pre-wrap; font-family: 'Monaco', 'Consolas', monospace; margin: 0;">${escapeHtml(ex.good)}</pre>
          </div>
        </div>` : ''}
      </div>`;
      });

      html += `
    </div>`;
    }

    html += `</div>`;
  } else if (metricLowerKey === 'inp' && deepINPResults) {
    html += `
    <div class="section" style="border: 1px dashed #47556980;">
      <div class="section-title">🔬 ${lang === 'cs' ? 'Hloubková INP analýza' : 'Deep INP Analysis'}</div>
      <div style="font-size: 12px; color: #94a3b8;">${lang === 'cs'
        ? `INP: nezměřeno — v tomto běhu nebyla zachycena platná interakce${deepINPResults.error ? ` (${escapeHtml(deepINPResults.error)})` : ''}. Field p75, pokud je dostupné, zůstává platné; laboratorní porovnání ani příčinu z tohoto běhu nelze určit.`
        : `INP: unavailable — no valid interaction was captured in this run${deepINPResults.error ? ` (${escapeHtml(deepINPResults.error)})` : ''}. Field p75, when available, remains valid; no lab comparison or cause can be inferred from this run.`}</div>
    </div>`;
  }

  if (inpCompareResults?.summary?.length) {
    const cmpLabels = {
      A: { cs: 'Mobil + přijmout cookies', en: 'Mobile + accept cookies' },
      B: { cs: 'Mobil + odmítnout cookies', en: 'Mobile + reject cookies' },
      C: { cs: 'Desktop + odmítnout cookies', en: 'Desktop + reject cookies' },
      D: { cs: 'Mobil + banner ponechán', en: 'Mobile + banner untouched' }
    };
    const isValidRun = (s) => (s.valid != null ? !!s.valid : (!!s.success && (s.cmpMode === 'none' || !!s.cmpClicked)));
    const allValid = inpCompareResults.summary.every(isValidRun);
    const rows = inpCompareResults.summary.map(s => {
      const valid = isValidRun(s);
      const inpColor = !valid ? '#64748b' : (s.inp || 0) <= 200 ? '#22c55e' : (s.inp || 0) <= 500 ? '#f59e0b' : '#ef4444';
      return `
        <tr style="border-bottom: 1px solid #334155;${valid ? '' : ' opacity: 0.65;'}">
          <td style="padding: 8px 12px; font-size: 12px; color: #cbd5e1;"><span style="color: #64748b; margin-right: 6px;">${s.key}</span>${escapeHtml(cmpLabels[s.key]?.[lang] || `${s.device} / ${s.cmpMode}`)}${s.cmpMode !== 'none' && s.success && !s.cmpClicked ? ` <span style="font-size: 10px; padding: 1px 6px; border-radius: 8px; background: #ef444420; color: #f87171;">${lang === 'cs' ? 'běh nevalidní — CMP nenalezeno' : 'invalid run — CMP not found'}</span>` : ''}</td>
          <td style="padding: 8px 12px; text-align: right; font-size: 13px; font-weight: 700; color: ${inpColor}; font-family: monospace;">${!s.success ? (lang === 'cs' ? 'chyba' : 'error') : !valid ? '—' : `${Math.round(s.inp || 0)}ms`}</td>
          <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #cbd5e1;">${valid ? s.longTasksCount : '—'}</td>
          <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: #cbd5e1;">${valid && s.thirdPartyShare != null ? `${Math.round(s.thirdPartyShare * 100)}%` : '—'}</td>
        </tr>`;
    }).join('');
    html += `
    <div class="section" style="border: 1px solid #10b98130;">
      <div class="section-title">⚖️ ${lang === 'cs' ? 'Porovnání 4 konfigurací (cookies × zařízení)' : '4-Configuration Comparison (cookies × device)'}</div>
      <table style="width: 100%; border-collapse: collapse; background: #1e293b; border-radius: 12px; overflow: hidden;">
        <thead>
          <tr style="border-bottom: 1px solid #475569;">
            <th style="padding: 8px 12px; text-align: left; font-size: 11px; color: #94a3b8; text-transform: uppercase;">${lang === 'cs' ? 'Konfigurace' : 'Configuration'}</th>
            <th style="padding: 8px 12px; text-align: right; font-size: 11px; color: #94a3b8; text-transform: uppercase;">INP</th>
            <th style="padding: 8px 12px; text-align: right; font-size: 11px; color: #94a3b8; text-transform: uppercase;">Long tasks</th>
            <th style="padding: 8px 12px; text-align: right; font-size: 11px; color: #94a3b8; text-transform: uppercase;">${lang === 'cs' ? '3. strany' : '3rd-party'}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="font-size: 11px; color: #64748b; margin-top: 10px;">${allValid
        ? (lang === 'cs'
          ? 'Rozdíl mezi konfiguracemi ukazuje vliv souhlasu i zařízení dohromady — nelze z něj izolovat jen marketingové skripty. Každá konfigurace je jeden běh (ne medián) a výsledek může vyjít oběma směry — odmítnutí někdy vede k více dlouhým úlohám (CMP zůstává aktivní, fallback logika), ne méně. Orientační porovnání, ne kontrolovaný experiment.'
          : 'The differences between configurations reflect consent and device combined — marketing scripts alone cannot be isolated from them. Each configuration is a single run (not a median) and the result can go either way — rejecting sometimes produces more long tasks (CMP stays active, fallback logic), not fewer. An indicative comparison, not a controlled experiment.')
        : (lang === 'cs'
          ? 'Část běhů není validní (cookie lišta nebyla nalezena, nebo běh selhal) — konfigurace proto nelze mezi sebou porovnávat. Nevalidní běhy jsou z interpretace vyloučeny.'
          : 'Some runs are invalid (the cookie banner was not found, or the run failed) — the configurations therefore cannot be compared. Invalid runs are excluded from interpretation.')}</p>
    </div>`;
  }

  if (multiPageINPResults) {
    const pages = multiPageINPResults.pages || multiPageINPResults.results || [];
    const validPages = pages.filter(p => p.inp != null && Number(p.inp) > 0
      && !p.error && p.measurementValid !== false && p.valid !== false
      && p.invalid !== true && p.status !== 'unavailable'
      && p.noValidInteraction !== true && p.interactionMeasured !== false
      && p.attributionValid !== false && p.targetsFound !== 0);
    const unmeasuredCount = pages.length - validPages.length;
    const avgINP = validPages.length > 0
      ? Math.round(validPages.reduce((s, p) => s + Number(p.inp), 0) / validPages.length)
      : null;
    const goodCount = validPages.filter(p => p.inp <= 200).length;
    const niCount = validPages.filter(p => p.inp > 200 && p.inp <= 500).length;
    const poorCount = validPages.filter(p => p.inp > 500).length;
    const inpValues = validPages.map(p => p.inp);
    const minINP = inpValues.length > 0 ? Math.min(...inpValues) : null;
    const maxINP = inpValues.length > 0 ? Math.max(...inpValues) : null;
    // Median is the honest central value for skewed page samples; a wide
    // range (worst ≥ 3× best or ≥ 4× median) calls for an indicative
    // summary; pages can have different repeat counts.
    const sortedINP = inpValues.slice().sort((a, b) => a - b);
    const midI = Math.floor(sortedINP.length / 2);
    const medINP = sortedINP.length ? Math.round(sortedINP.length % 2 ? sortedINP[midI] : (sortedINP[midI - 1] + sortedINP[midI]) / 2) : null;
    const wideINPRange = (minINP > 0 && maxINP / minINP > 2) || (medINP > 0 && maxINP / medINP >= 3);
    const avgDom = pages.length > 0 ? Math.round(pages.reduce((s, p) => s + (p.domSize || 0), 0) / pages.length) : 0;
    const avgLT = pages.length > 0 ? Math.round(pages.reduce((s, p) => s + (p.longTasksCount ?? p.longTasks?.length ?? 0), 0) / pages.length) : 0;

    html += `
    <div class="section">
      <div class="section-title">🌐 ${lang === 'cs' ? 'Vícestránková INP analýza' : 'Multi-Page INP Analysis'}</div>
        <div style="font-size:12px;color:${validPages.length ? '#94a3b8' : '#f59e0b'};margin-bottom:14px;padding:10px 12px;background:#1e293b;border-radius:8px;">
          ${validPages.length
            ? (lang === 'cs'
              ? `Platně změřeno ${validPages.length} z ${pages.length} stránek; ${unmeasuredCount} nezměřených se do statistik nepočítá. Pásma níže hodnotí agregát každé stránky, nikoli každý jednotlivý běh; bez jednotlivých hodnot nelze tvrdit, že všechny běhy byly dobré.`
              : `Validly measured ${validPages.length} of ${pages.length} pages; ${unmeasuredCount} unmeasured pages are excluded from statistics. Bands below classify each page aggregate, not every individual run; without individual values not all runs can be claimed good.`)
            : (lang === 'cs'
              ? `Žádná z ${pages.length} stránek neposkytla platné INP s ověřenou interakcí a atribucí. INP sweepu není k dispozici; nuly nejsou „dobré" a výsledek neznamená, že se problém nereprodukoval.`
              : `None of ${pages.length} pages produced valid INP with a verified interaction and attribution. Sweep INP is unavailable; zeroes are not "good" and this does not mean the issue was not reproduced.`)}
        </div>

      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px;">
        <div style="background: #05966920; border: 1px solid #05966950; border-radius: 12px; padding: 16px; text-align: center;">
          <div style="font-size: 28px; font-weight: 700; color: #22c55e;">${goodCount}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'Dobré' : 'Good'}</div>
        </div>
        <div style="background: #d9770620; border: 1px solid #d9770650; border-radius: 12px; padding: 16px; text-align: center;">
          <div style="font-size: 28px; font-weight: 700; color: #f59e0b;">${niCount}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'K zlepšení' : 'Needs Improvement'}</div>
        </div>
        <div style="background: #dc262620; border: 1px solid #dc262650; border-radius: 12px; padding: 16px; text-align: center;">
          <div style="font-size: 28px; font-weight: 700; color: #ef4444;">${poorCount}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'Špatné' : 'Poor'}</div>
        </div>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding: 12px 16px; background: #1e293b; border-radius: 8px;">
        <div>
          <span style="font-size: 12px; color: #94a3b8;">${lang === 'cs' ? 'Medián INP:' : 'Median INP:'}</span>
           <span style="font-size: 16px; font-weight: 700; color: ${medINP == null ? '#94a3b8' : medINP <= 200 ? '#22c55e' : medINP <= 500 ? '#f59e0b' : '#ef4444'}; margin-left: 8px;">${medINP == null ? '—' : `${medINP}ms`}</span>
           <span style="font-size: 11px; color: #64748b; margin-left: 8px;">(${lang === 'cs' ? 'průměr' : 'average'} ${avgINP == null ? '—' : `${avgINP}ms`})</span>
        </div>
        <div style="font-size: 12px; color: #94a3b8;">${lang === 'cs' ? 'Rozsah:' : 'Range:'} <span style="font-weight: 600; color: #e2e8f0;">${minINP == null ? '—' : `${minINP}ms - ${maxINP}ms`}</span></div>
      </div>
      ${wideINPRange ? `<div style="font-size: 11px; color: #f59e0b; margin: -8px 0 16px; padding: 8px 16px; background: #d9770610; border: 1px solid #d9770630; border-radius: 8px;">${lang === 'cs'
        ? '⚠ Rozsah hodnot je široký — počty běhů se mezi stránkami liší; medián i průměr napříč stránkami berte orientačně. U každé stránky zkontrolujte počet validních a nevalidních pokusů.'
        : '⚠ The value range is wide — run counts differ between pages; treat the site-wide median and average as indicative. Check valid and invalid attempt counts for each page.'}</div>` : ''}

      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 20px;">
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 12px; text-align: center;">
          <div style="font-size: 10px; color: #94a3b8;">${lang === 'cs' ? 'Prům. DOM' : 'Avg. DOM'}</div>
          <div style="font-size: 18px; font-weight: 700; color: ${avgDom > 1500 ? '#f59e0b' : '#22c55e'};">${avgDom}</div>
        </div>
        <div style="background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 12px; text-align: center;">
          <div style="font-size: 10px; color: #94a3b8;">${lang === 'cs' ? 'Prům. Long Tasks' : 'Avg. Long Tasks'}</div>
          <div style="font-size: 18px; font-weight: 700; color: ${avgLT > 5 ? '#f59e0b' : '#22c55e'};">${avgLT}</div>
        </div>
      </div>`;

    pages.forEach((page, i) => {
      const measured = validPages.includes(page);
      const pageInp = measured ? Number(page.inp) : null;
      const pageColor = !measured ? '#64748b' : pageInp <= 200 ? '#22c55e' : pageInp <= 500 ? '#f59e0b' : '#ef4444';
      const pagePath = (() => {
        try { return new URL(page.url || '').pathname || '/'; } catch { return page.url || '/'; }
      })();

      html += `
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; background: #1e293b; border-radius: 8px; margin-bottom: 6px; border-left: 3px solid ${pageColor};">
        <div style="font-size: 12px; color: #cbd5e1; word-break: break-all; flex: 1; margin-right: 12px;">${escapeHtml(pagePath)}</div>
        <div style="display: flex; align-items: center; gap: 12px; white-space: nowrap;">
          <span style="font-size: 11px; color: #64748b;">DOM: ${page.domSize || 0}</span>
          <span style="font-size: 14px; font-weight: 700; color: ${pageColor};">${measured ? `${Math.round(pageInp)}ms` : (lang === 'cs' ? 'nezměřeno' : 'unmeasured')}</span>
          <span style="width: 10px; height: 10px; border-radius: 50%; background: ${pageColor};"></span>
        </div>
      </div>`;
    });

    html += renderMultiPageInteractionDiagnosticsHtml(pages, lang, 'dark');

    if (multiPageINPResults.worstPage) {
      const wp = multiPageINPResults.worstPage;
      html += `
      <div style="margin-top: 16px; padding: 12px 16px; background: #ef444410; border: 1px solid #ef444430; border-radius: 8px;">
        <span style="font-size: 12px; color: #fca5a5; font-weight: 600;">${lang === 'cs' ? 'Nejhorší stránka:' : 'Worst page:'}</span>
        <span style="font-size: 12px; color: #e2e8f0; margin-left: 8px;">${escapeHtml(wp.url || '')}</span>
        <span style="font-size: 14px; font-weight: 700; color: #ef4444; margin-left: 12px;">${Math.round(wp.inp || 0)}ms</span>
      </div>`;
    }

    if (multiPageINPResults.duration) {
      html += `
      <div style="text-align: center; margin-top: 12px; font-size: 11px; color: #64748b;">
        ${lang === 'cs' ? 'Analýza dokončena za' : 'Analysis completed in'} ${(multiPageINPResults.duration / 1000).toFixed(1)}s
      </div>`;
    }

    html += `</div>`;
  }

  if (multiPageLCPResults) {
    const pages = multiPageLCPResults.pages || [];
    const summary = multiPageLCPResults.summary || {};
    const validPages = pages.filter(p => p.lcp != null && p.lcp > 0 && !p.error);
    const goodCount = summary.goodPages ?? validPages.filter(p => p.lcp <= 2500).length;
    const niCount = summary.needsImprovementPages ?? validPages.filter(p => p.lcp > 2500 && p.lcp <= 4000).length;
    const poorCount = summary.poorPages ?? validPages.filter(p => p.lcp > 4000).length;
    const lcpValues = validPages.map(p => p.lcp);
    const avgLCP = summary.avgLCP ?? (lcpValues.length > 0 ? Math.round(lcpValues.reduce((s, v) => s + v, 0) / lcpValues.length) : 0);
    const minLCP = summary.minLCP ?? (lcpValues.length > 0 ? Math.min(...lcpValues) : 0);
    const maxLCP = summary.maxLCP ?? (lcpValues.length > 0 ? Math.max(...lcpValues) : 0);
    const sortedPages = [...pages].sort((a, b) => (b.lcp || 0) - (a.lcp || 0));

    html += `
    <div class="section">
      <div class="section-title">🌐 ${lang === 'cs' ? 'Vícestránková LCP analýza' : 'Multi-Page LCP Analysis'}</div>

      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px;">
        <div style="background: #05966920; border: 1px solid #05966950; border-radius: 12px; padding: 16px; text-align: center;">
          <div style="font-size: 28px; font-weight: 700; color: #22c55e;">${goodCount}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'Dobré' : 'Good'}</div>
        </div>
        <div style="background: #d9770620; border: 1px solid #d9770650; border-radius: 12px; padding: 16px; text-align: center;">
          <div style="font-size: 28px; font-weight: 700; color: #f59e0b;">${niCount}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'K zlepšení' : 'Needs Improvement'}</div>
        </div>
        <div style="background: #dc262620; border: 1px solid #dc262650; border-radius: 12px; padding: 16px; text-align: center;">
          <div style="font-size: 28px; font-weight: 700; color: #ef4444;">${poorCount}</div>
          <div style="font-size: 11px; color: #94a3b8;">${lang === 'cs' ? 'Špatné' : 'Poor'}</div>
        </div>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding: 12px 16px; background: #1e293b; border-radius: 8px;">
        <div>
          <span style="font-size: 12px; color: #94a3b8;">${lang === 'cs' ? 'Průměrné LCP:' : 'Average LCP:'}</span>
          <span style="font-size: 16px; font-weight: 700; color: ${avgLCP <= 2500 ? '#22c55e' : avgLCP <= 4000 ? '#f59e0b' : '#ef4444'}; margin-left: 8px;">${formatLcpMs(avgLCP)}</span>
        </div>
        <div style="font-size: 12px; color: #94a3b8;">${lang === 'cs' ? 'Rozsah:' : 'Range:'} <span style="font-weight: 600; color: #e2e8f0;">${formatLcpMs(minLCP)} - ${formatLcpMs(maxLCP)}</span></div>
      </div>`;

    sortedPages.forEach((page) => {
      const pageLcp = page.lcp || 0;
      const isError = !!page.error;
      const status = page.status || (pageLcp <= 2500 ? 'good' : pageLcp <= 4000 ? 'needs-improvement' : 'poor');
      const pageColor = isError ? '#64748b' : (status === 'good' ? '#22c55e' : status === 'needs-improvement' ? '#f59e0b' : '#ef4444');
      const pagePath = (() => {
        try { return new URL(page.url || '').pathname || '/'; } catch { return page.url || '/'; }
      })();
      const causeCount = Array.isArray(page.causes) ? page.causes.length : 0;

      html += `
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; background: #1e293b; border-radius: 8px; margin-bottom: 6px; border-left: 3px solid ${pageColor};">
        <div style="font-size: 12px; color: #cbd5e1; word-break: break-all; flex: 1; margin-right: 12px;">${escapeHtml(pagePath)}</div>
        <div style="display: flex; align-items: center; gap: 12px; white-space: nowrap;">
          ${causeCount > 0 ? `<span style="font-size: 11px; color: #64748b;">${causeCount} ${lang === 'cs' ? 'příčin' : 'causes'}</span>` : ''}
          <span style="font-size: 14px; font-weight: 700; color: ${pageColor};">${isError ? 'Error' : formatLcpMs(pageLcp)}</span>
          <span style="width: 10px; height: 10px; border-radius: 50%; background: ${pageColor};"></span>
        </div>
      </div>`;
    });

    const worst = summary.worstPage || multiPageLCPResults.worstPage;
    if (worst) {
      html += `
      <div style="margin-top: 16px; padding: 12px 16px; background: #ef444410; border: 1px solid #ef444430; border-radius: 8px;">
        <span style="font-size: 12px; color: #fca5a5; font-weight: 600;">${lang === 'cs' ? 'Nejhorší stránka:' : 'Worst page:'}</span>
        <span style="font-size: 12px; color: #e2e8f0; margin-left: 8px; word-break: break-all;">${escapeHtml(worst.url || '')}</span>
        <span style="font-size: 14px; font-weight: 700; color: #ef4444; margin-left: 12px;">${formatLcpMs(worst.lcp || 0)}</span>
      </div>`;
    }

    if (multiPageLCPResults.duration) {
      html += `
      <div style="text-align: center; margin-top: 12px; font-size: 11px; color: #64748b;">
        ${lang === 'cs' ? 'Analýza dokončena za' : 'Analysis completed in'} ${(multiPageLCPResults.duration / 1000).toFixed(1)}s
      </div>`;
    }

    html += `</div>`;
  }

  if (Array.isArray(savingsRecommendations) && savingsRecommendations.length > 0) {
    const sl = getSavingsLabels(lang);
    // Estimates overlap and are NOT additive — show the largest single
    // estimate ("up to ~X") instead of a misleading sum.
    const recMax = (unit) => savingsRecommendations
      .filter(r => r.estimatedSavings?.unit === unit && r.estimatedSavings?.headlineEligible !== false)
      .reduce((m, r) => Math.max(m, r.estimatedSavings.value || 0), 0);
    const maxMs = Math.round(savingsSummary?.maxPotentialSavingsMs ?? recMax('ms'));
    const maxKb = Math.round((savingsSummary?.maxPotentialSavingsBytes ?? recMax('bytes')) / 1024);
    const totalParts = [];
    if (maxMs > 0) totalParts.push(`~${maxMs} ms`);
    if (maxKb > 0) totalParts.push(`~${maxKb} KB`);
    // Aggregate = measured opportunity / model estimate for this load
    // scenario, never a guaranteed directly-removable amount.
    if (totalParts.length) totalParts[totalParts.length - 1] += ` (${sl.opportunityNote})`;

    const impactLabelOf = (k) => sl[`impact${k.charAt(0).toUpperCase()}${k.slice(1)}`] || k;
    const effortLabelOf = (k) => sl[`effort${k.charAt(0).toUpperCase()}${k.slice(1)}`] || k;
    const badgeColor = (rec) => rec.confidenceBadge?.color || 'yellow';
    const badgeStyles = {
      green: 'background:rgba(34,197,94,0.12);color:#86efac;border:1px solid rgba(34,197,94,0.3);',
      yellow: 'background:rgba(234,179,8,0.12);color:#fde68a;border:1px solid rgba(234,179,8,0.3);',
      orange: 'background:rgba(249,115,22,0.12);color:#fdba74;border:1px solid rgba(249,115,22,0.3);'
    };
    const impactStyles = {
      high: 'background:rgba(239,68,68,0.12);color:#fca5a5;',
      medium: 'background:rgba(234,179,8,0.12);color:#fde68a;',
      low: 'background:rgba(59,130,246,0.12);color:#93c5fd;'
    };

    html += `
    <div class="section" style="background:linear-gradient(135deg,#1e3a5f 0%,#0f172a 100%);border:1px solid #22c55e40;">
      <div class="section-title" style="color:#86efac;display:flex;justify-content:space-between;align-items:center;text-transform:none;letter-spacing:0;">
        <span>💚 ${sl.title}${totalParts.length ? ` <span style="font-size:11px;color:#94a3b8;font-weight:400">· ${sl.total} ${totalParts.join(' · ')}</span>` : ''}</span>
        <span style="font-size:11px;color:#64748b;font-weight:400">${savingsRecommendations.length} ${sl.countSuffix}</span>
      </div>
      ${totalParts.length ? `<div style="font-size:11px;color:#94a3b8;margin:-4px 0 12px;">${escapeHtml(sl.nonAdditive)}</div>` : ''}`;

    const ownerWord = lang === 'cs' ? 'vlastník' : 'owner';
    savingsRecommendations.forEach((rec) => {
      const est = rec.estimatedSavings;
      const range = formatSavingsRange(est);
      const bk = savingsBadgeKey(rec);
      const bColor = badgeColor(rec);
      const owner = ownerForRecommendation(rec, lang, { hasGtmContainer: containerEvidence });
      html += `
      <div style="background:#1e293b;border:1px solid #334155;border-radius:8px;padding:14px;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start;">
          <div style="flex:1;min-width:0">
            <div style="font-size:14px;font-weight:600;color:#f1f5f9;margin-bottom:6px;">${escapeHtml(rec.title || rec.id || '')}</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.04em;padding:2px 8px;border-radius:4px;${badgeStyles[bColor] || badgeStyles.yellow}">${escapeHtml(sl[bk])}</span>
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.04em;padding:2px 8px;border-radius:4px;${impactStyles[rec.impact] || impactStyles.low}">${escapeHtml(impactLabelOf(rec.impact || 'low'))}</span>
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.04em;color:#94a3b8;">${escapeHtml(sl.effort)}: ${escapeHtml(effortLabelOf(rec.effort || 'medium'))}</span>
              ${owner ? `<span style="font-size:10px;text-transform:uppercase;letter-spacing:0.04em;padding:2px 8px;border-radius:4px;border:1px solid ${owner.color}55;color:${owner.color};">${escapeHtml(ownerWord)}: ${escapeHtml(owner.label)}</span>` : ''}
            </div>
            ${owner ? `<div style="font-size:11px;color:#94a3b8;line-height:1.5;margin-top:6px;">${escapeHtml(owner.procedure)}</div>` : ''}
          </div>
          <div style="text-align:right;flex-shrink:0;min-width:100px;">
            <div style="font-size:14px;font-weight:700;color:#4ade80;">${escapeHtml(formatSavingsValue(est))}</div>
            ${range ? `<div style="font-size:10px;color:#64748b;margin-top:2px;">${escapeHtml(sl.range)} ${escapeHtml(range)}</div>` : ''}
          </div>
        </div>
      </div>`;
    });

    html += `
      <div style="margin-top:12px;padding:12px;background:#0f172a;border-radius:8px;border-left:3px solid #22c55e;">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:#86efac;margin-bottom:6px;font-weight:600;">${escapeHtml(sl.howComputed)}</div>
        <div style="font-size:12px;color:#cbd5e1;line-height:1.55;">${escapeHtml(sl.methodology)}</div>
      </div>
    </div>`;
  }

  {
    const gaps = [];
    if (primaryDeepFailed) {
      gaps.push(lang === 'cs'
        ? `Hloubkový test hlavní metriky ${metricUpperKey} selhal. Report proto nemá platnou laboratorní hodnotu ani rozpad příčiny hlavního problému.`
        : `The deep test for the primary metric ${metricUpperKey} failed. The report therefore has no valid lab value or cause breakdown for the primary problem.`);
    }
    const rawSweepPages = primarySweep?.results?.length ? primarySweep.results
      : primarySweep?.pages?.length ? primarySweep.pages
        : primarySweep?.pageResults?.length ? primarySweep.pageResults
          : [];
    if (rawSweepPages.length) {
      const valueOf = page => page?.value ?? page?.inp ?? page?.lcp ?? page?.cls ?? page?.ttfb;
      const valid = rawSweepPages.filter(page => {
        const value = Number(valueOf(page));
        if (page?.error || page?.success === false || page?.measurementValid === false
            || page?.valid === false || page?.invalid === true || !Number.isFinite(value)) return false;
        if (metricLowerKey === 'inp' && (!(value > 0) || page?.noValidInteraction === true
            || page?.interactionMeasured === false || page?.attributionValid === false
            || page?.targetsFound === 0)) return false;
        return true;
      }).length;
      const missing = rawSweepPages.length - valid;
      if (missing > 0) gaps.push(lang === 'cs'
        ? `Sweep hlavní metriky platně změřil ${valid} z ${rawSweepPages.length} stránek; ${missing} nezměřených stránek není zahrnuto do statistik.`
        : `The primary-metric sweep validly measured ${valid} of ${rawSweepPages.length} pages; ${missing} unmeasured pages are excluded from the statistics.`);
    }
    const dpItems = Array.isArray(devicePriority?.items) ? devicePriority.items : [];
    const fieldMetrics = [...new Set(dpItems.map(item => item.metric).filter(Boolean))];
    const fractions = devicePriority?.cruxFormFactorFractions || {};
    const availability = devicePriority?.deviceAvailability || {};
    const deviceName = key => key === 'phone'
      ? (lang === 'cs' ? 'Mobil' : 'Mobile')
      : key === 'desktop' ? 'Desktop' : 'Tablet';
    for (const key of ['phone', 'desktop', 'tablet']) {
      if (!(Number(fractions[key]) > 0 || availability[key]?.available === false)) continue;
      const missingMetrics = fieldMetrics.filter(fieldMetric => !dpItems.some(item =>
        item.device === key && item.metric === fieldMetric && Number.isFinite(Number(item.p75))));
      if (!missingMetrics.length) continue;
      const reasonCode = availability[key]?.reason;
      const reason = reasonCode === 'no_field_data'
        ? (lang === 'cs'
          ? 'Dotaz pro tento běh nevrátil dostatek device-specific CrUX dat; nejde o tvrzení, že data nemohou existovat jindy.'
          : 'The query returned insufficient device-specific CrUX data for this run; this does not claim that data can never be available.')
        : reasonCode === 'query_failed'
          ? (lang === 'cs'
            ? 'Device-specific CrUX dotaz selhal; nepřítomnost field dat proto není potvrzena.'
            : 'The device-specific CrUX query failed, so absence of field data is not established.')
          : (lang === 'cs'
            ? 'Důvod z dostupných dat nelze určit.'
            : 'The available data does not establish why.');
      gaps.push(lang === 'cs'
        ? `Field p75 pro zařízení ${deviceName(key)} v tomto běhu chybí u metrik ${missingMetrics.map(item => String(item).toUpperCase()).join(', ')}. ${reason}`
        : `Field p75 for ${deviceName(key)} is missing in this run for ${missingMetrics.map(item => String(item).toUpperCase()).join(', ')}. ${reason}`);
    }
    if (gaps.length) {
      html += `
      <div class="section" style="border:1px solid #f59e0b70;">
        <div class="section-title" style="color:#fbbf24;">${lang === 'cs' ? 'Známé mezery v datech' : 'Acknowledged data gaps'}</div>
        <div style="font-size:12px;color:#cbd5e1;line-height:1.65;">${gaps.map(gap => `• ${escapeHtml(gap)}`).join('<br>')}</div>
      </div>`;
    }
  }

  {
    const baseDate = (auditDate && !isNaN(new Date(auditDate))) ? new Date(auditDate) : new Date();
    const fl = followUpLabels(lang);
    const schedule = buildFollowUpSchedule(baseDate);
    const scheduleAudit = schedule.auditDate;
    const fieldDate = schedule.fieldDate;
    html += `
    <div class="section" style="border:1px solid #3b82f640;">
      <div class="section-title" style="color:#93c5fd;display:flex;justify-content:space-between;align-items:center;text-transform:none;letter-spacing:0;">
        <span>📅 ${escapeHtml(fl.title)}</span>
        <span style="font-size:11px;color:#64748b;font-weight:400">${escapeHtml(fl.auditDateLabel)}: ${escapeHtml(formatFollowUpDate(scheduleAudit, lang))}</span>
      </div>
      <div style="background:#1e293b;border:1px solid #334155;border-radius:8px;padding:14px;margin-bottom:10px;">
        <div style="font-size:13px;font-weight:600;color:#f1f5f9;margin-bottom:6px;">1. ${escapeHtml(fl.labTitle)}</div>
        <div style="font-size:12px;color:#94a3b8;line-height:1.55;">${escapeHtml(fl.labText)}</div>
      </div>
      <div style="background:#1e293b;border:1px solid #334155;border-radius:8px;padding:14px;margin-bottom:10px;">
        <div style="font-size:13px;font-weight:600;color:#f1f5f9;margin-bottom:6px;">2. ${escapeHtml(fl.fieldTitle)} — <span style="color:#93c5fd;">${escapeHtml(formatFollowUpDate(fieldDate, lang))}</span></div>
        <div style="font-size:12px;color:#94a3b8;line-height:1.55;">${escapeHtml(fl.fieldTextBefore)} <strong style="color:#e2e8f0;">${escapeHtml(formatFollowUpDate(fieldDate, lang))}</strong> ${escapeHtml(fl.fieldTextAfter)}</div>
      </div>
      <div style="padding:12px;background:#0f172a;border-radius:8px;border-left:3px solid #3b82f6;">
        <div style="font-size:12px;color:#cbd5e1;line-height:1.55;">${escapeHtml(fl.windowExplain)}</div>
      </div>
    </div>`;
  }

  html += `
    <div class="footer">
      <p>${lang === 'cs' ? 'Vygenerováno pomocí' : 'Generated by'} ControlVitals - controlvitals.com</p>
      <p style="margin-top: 8px;">${lang === 'cs' ? 'Tento report obsahuje automaticky detekované problémy a doporučení.' : 'This report contains automatically detected issues and recommendations.'}</p>
    </div>
  </div>

  <!-- Lightbox Overlay -->
  <div id="lightbox-overlay" class="lightbox-overlay" onclick="closeLightbox()">
    <span class="lightbox-close" onclick="closeLightbox()">&times;</span>
    <img id="lightbox-image" src="" alt="${lang === 'cs' ? 'Zvětšený obrázek' : 'Enlarged image'}">
  </div>

  <script>
    function openLightbox(src) {
      var overlay = document.getElementById('lightbox-overlay');
      var img = document.getElementById('lightbox-image');
      img.src = src;
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      var overlay = document.getElementById('lightbox-overlay');
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        closeLightbox();
      }
    });
  </script>
</body>
</html>`;

  const evidenceHTML = renderEvidenceHTML({
    metric, deepCLSResults, deepINPResults, deepLCPResults, deepTTFBResults
  }, lang, 'dark');
  const contractHTML = renderAuditEvidenceContractHTML(evidenceContract, lang, 'dark');
  const scenarioHTML = renderInpScenarioScopesHTML({
    url, device, deepAnalyzedUrl, deepINPResults, deepDeviceRuns, evidenceContract
  }, lang, 'dark');
  return embedAuditEvidenceDigest(evidenceHTML || contractHTML || scenarioHTML
    ? html.replace('</body>', `${renderInpScenarioFailureNotice({ deepDeviceRuns, evidenceContract }, lang, 'dark')}${scenarioHTML}${evidenceHTML}${contractHTML}</body>`) : html, evidenceContract);
};

export const downloadHTML = (html, filename = 'diagnostics-report.html') => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generatePDF = async (containerElement, filename = 'diagnostics-report.pdf', options = {}) => {
  const { language = 'cs' } = options;

  try {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);
    const canvas = await html2canvas(containerElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#0f172a',
      logging: false,
      windowWidth: 1200,
      onclone: (clonedDoc) => {
        const container = clonedDoc.querySelector('[data-export-container]') || clonedDoc.body.firstElementChild;
        if (container) {
          container.style.width = '1200px';
          container.style.maxWidth = '1200px';
        }
      }
    });

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(filename);
    return true;
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
};

export const captureScreenshotFromElement = async (element) => {
  try {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#0f172a',
      logging: false
    });
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Screenshot capture error:', error);
    return null;
  }
};
